import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { aufgabeAusPlan, aufgabenVorbelegung } from "@/lib/aufgabenVorbelegung"
import { useCacheMitleser } from "@/lib/cacheMitleser"
import { wegSchritt } from "@/lib/wege"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  ArrowLeft, CalendarClock, CheckCircle, ChevronLeft,
  ChevronDown, ExternalLink, Flag, Inbox,
  Mail as MailIcon, Paperclip, Pencil, Plus, RefreshCw, Search, Send,
  ShieldAlert, StickyNote, Trash2,
} from "lucide-react"
import { api } from "@/lib/api"
import { Loading } from "@/components/portal/LoadingError"
import { AttachmentsSection } from "@/components/portal/AttachmentsSection"
// Eine EmailBody fuer alle. Der Posteingang hatte lange eine eigene Kopie —
// am 06.08.2026 lief deshalb ein Bildfix ins Leere, weil er nur die geteilte
// Fassung traf. Nie wieder eine zweite.
import { EmailBody } from "@/components/portal/EmailBody"
import { InhaltEntferntHinweis } from "@/components/portal/InhaltEntfernt"
import {
  avatarColor, cleanSender, extractEmail,
  fmtDate, fmtDateLong, prettySender, senderInitials,
} from "./emails/format"
import { FolderItem, HERKUNFT_MIME, StandardFolderItem } from "./emails/FolderItems"
import { ComposeModal } from "./emails/ComposeModal"
import { AntwortAusserhalbVerlauf, type AntwortAusserhalb } from "./emails/AntwortAusserhalbVerlauf"
import { getDraft, type MailDraft } from "@/lib/draftModals"
import { ToolbarSearch } from "./emails/ToolbarSearch"
import { MailIdMarke } from "@/components/portal/MailIdMarke"
import { HeroContactProjects, HeroLinkActions } from "./emails/HeroSection"
import { fetchEmails, MAILS_MAX, MAILS_SCHRITT, type EmailItem } from "./emails/data"
import { ActivityLog, MailVerlauf, groupByDay, ThreadTimeline } from "./emails/ThreadTimeline"
import { mailStand, STAND_FARBE, STAND_TEXT } from "./emails/status"
import { ActionBar, extractReplyAddress, istSpamOrdner, parseContactPrefill } from "./emails/ActionBar"
import { KarteKopf } from "./emails/KarteKopf"
import { PostfachKarte, usePostfachModus } from "./emails/PostfachKarte"
import { KarteFuss } from "./emails/KarteFuss"

/** Ordner-Wert „Spam" — dieselbe Konstante wie `database/emails.py` SPAM_ORDNER_FILTER. */
const SPAM_ORDNER = "__SPAM__"
import { ForwardForm } from "@/components/portal/ForwardForm"
import { ReplyForm } from "@/components/portal/ReplyForm"
import { HeroLinkModal } from "@/components/portal/HeroLinkModal"
import { HistoryContextBadge } from "@/components/portal/HistoryContextBadge"
import { FlagNoteModal } from "@/components/portal/FlagNoteModal"
import { PendingCleanupCard } from "@/components/portal/PendingCleanupCard"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { AnsprechpartnerModal } from "@/components/portal/AnsprechpartnerModal"
import { useContactDraftRestore } from "@/hooks/useContactDraftRestore"
import { BookingModal } from "@/components/portal/BookingModal"
import { terminBlockerFragen } from "@/lib/terminBlocker"
import { HeroInfoPopover } from "@/components/portal/HeroInfoPopover"
import { FortytoolsLinkCard } from "@/components/portal/FortytoolsLinkCard"
import { LinkSuggestionsBox, PlanVorgangZeile, type LinkSuggestions } from "@/components/portal/LinkSuggestions"
import { InboxActionPlan } from "@/components/portal/InboxActionPlan"
import { PlanAusgangBlock } from "@/components/portal/PlanAusgangBlock"
import { HeroAbgleich } from "@/components/portal/HeroAbgleich"
import { LeistungsspektrumBlock } from "@/components/portal/LeistungsspektrumBlock"
import { BetriebswissenBlock } from "@/components/portal/BetriebswissenBlock"
import { VorgehenChip } from "@/components/portal/VorgehenChip"
import { toast } from "@/components/portal/Toast"
import { OffenerAnrufHinweis } from "@/components/portal/OffenerAnrufHinweis"
import { TerminBestaetigtBanner, type TerminBestaetigt } from "@/components/portal/TerminBestaetigtBanner"
import { LetzterKontakt } from "@/components/portal/LetzterKontakt"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { useMandantWechsel } from "@/hooks/useMandantWechsel"
import { DENSITIES, densityClass, useListDensity } from "@/hooks/useListDensity"
import { cn, stripAttachmentDumps } from "@/lib/utils"
import { terminEinladungLesen, istUebernehmbar, dauerAuswahl } from "@/lib/terminEinladung"
import { personenImText, ohneAbsender } from "@/lib/personenImText"
import { platzhalterEntfernen } from "@/lib/signaturBilder"
import { SpamModal } from "./emails/SpamModal"
import { PostfachTreffer } from "./emails/PostfachSuche"
import { HAUPT, postfachName, postfachQuery, usePostfachWahl } from "./emails/postfach"
import type { EmailAntwort } from "@/lib/emailQuery"
import type { ActionPlan } from "@/components/review/types"
import { HINWEIS_PUNKTE, NochOffenLeiste } from "./emails/NochOffenLeiste"
import { fahneUmschalten } from "./emails/fahne"

type EmailListe = Awaited<ReturnType<typeof fetchEmails>>
import { NotizenPanel } from "./emails/NotizenPanel"
import { UebergabeDialog } from "./emails/UebergabeDialog"
import { PersonPunkt } from "./emails/Person"
import { AnsichtReiter } from "./emails/AnsichtReiter"
import { anzeigeName, tasteZaehlt, useAnsichtZahlen, type Ansicht } from "./emails/zusammenarbeit"
import { useGelesen } from "./emails/gelesen"

/** #193: Kategorien, die immer als Filter-Chip erscheinen (auch bei 0 Treffern). */
const FESTE_KATEGORIEN = ["Angebotsannahme"] as const

/** 3-Pane E-Mail-Layout — Folders | List | Detail. 1:1 wie admin/emails.py. */
/**
 * Die Seite /emails (Paul 28.10.2026): standardmäßig das neue Postfach mit den
 * Töpfen „Ein Klick“, „Von Hand“, „automatisch erledigt“. „Bisherige Ansicht“
 * ist die Mail-Liste von vorher — je Person gemerkt, jederzeit umschaltbar.
 */
export default function EmailsSeite() {
  const [modus, setModus] = usePostfachModus()
  const [params, setParams] = useSearchParams()
  // Ein wiederhergestellter Entwurf (Modal-Restore) braucht die Liste. Suche
  // (?q=) und „Alle Mails" (?alle=1) aus dem neuen Postfach öffnen sie nur
  // vorübergehend — die eigene Wahl bleibt, wie sie ist.
  const nurDiesmal = !!(params.get("q") || params.get("alle"))
  if (modus === "liste" || params.get("restore_draft") || nurDiesmal) {
    return <EmailsListeBisher onNeuesPostfach={() => {
      const p = new URLSearchParams(params)
      p.delete("q"); p.delete("alle")
      setParams(p, { replace: true })
      setModus("karte")
    }} />
  }
  return <PostfachKarte onBisherige={() => setModus("liste")} />
}

function EmailsListeBisher({ onNeuesPostfach }: { onNeuesPostfach?: () => void }) {
  const [tenant, setTenant] = useTenantFilter()
  // Mehrere Postfächer je Mandant: welches wird gerade angezeigt? "" = alle.
  const pf = usePostfachWahl(tenant)
  const [density, setDensity] = useListDensity()
  const [folderFilter, setFolderFilter] = useState("")
  const [spamOffen, setSpamOffen] = useState(false)  // #55
  /** Ordnerleiste eingeklappt — gemerkt wie bei der Hauptnavigation, damit
   *  man sie am kleinen Bildschirm nicht bei jedem Aufruf neu wegklickt. */
  // Ordnerspalte: eingeklappt ab hier, weil sich darunter Ordner, Liste und
  // Vorschau nicht mehr sinnvoll teilen lassen. Gemessen 23.08.2026: Bei
  // 1280 px blieben der Betreffzeile 103 von 368 px Listenbreite.
  const ORDNER_ZU_AB = 1500
  // Seit 14.10.2026 (Variante A): die Ordner stehen standardmässig im Menü
  // „Posteingang ▾" über der Liste — die Spalte kostete Platz, den die Mail
  // braucht. Wer sie angeheftet hat („ev2_ordner" = auf), behält sie.
  const [ordnerEingeklappt, setOrdnerEingeklappt] = useState(() => {
    const g = localStorage.getItem("ev2_ordner")
    if (g === "auf") return false
    return true
  })
  // Die klassische Ansicht (vor Variante A) ist seit 28.10.2026 weg (Paul):
  // „Bisherige Ansicht" ist jetzt DIESE Liste, das neue Postfach steht davor.
  /** Ordner als Aufklapp-Menü über der Liste (bei eingeklappter Spalte). */
  const [ordnerMenue, setOrdnerMenue] = useState(false)
  /** Reiter über der Liste: offen | meine | wartet | kunde_wartet | alle (Marlene 14.10.2026, #93). */
  const [ansicht, setAnsichtRoh] = useState<Ansicht>(() => {
    const g = localStorage.getItem("ev2_ansicht")
    return g === "meine" || g === "wartet" || g === "kunde_wartet" || g === "alle" ? g : "offen"
  })
  /** Personen-Filter (#198): offene Mails dieser Person. Nur für diese Sitzung gemerkt. */
  const [person, setPerson] = useState<string | null>(null)
  const setAnsicht = (a: Ansicht) => {
    setPerson(null)
    setAnsichtRoh(a)
    try { localStorage.setItem("ev2_ansicht", a) } catch { /* privat */ }
  }
  // Wie bei der Hauptnavigation: Eine bewusste Entscheidung gilt, bis das
  // Fenster die Schwelle wechselt — dann greift wieder die Automatik.
  useEffect(() => {
    let warSchmal = window.innerWidth < ORDNER_ZU_AB
    const onResize = () => {
      const schmal = window.innerWidth < ORDNER_ZU_AB
      if (schmal !== warSchmal) {
        warSchmal = schmal
        // Nur eine angeheftete Spalte klappt am schmalen Fenster weg.
        if (schmal) setOrdnerEingeklappt(true)
        else setOrdnerEingeklappt(localStorage.getItem("ev2_ordner") !== "auf")
      }
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])
  const [folder, setFolder] = useState("INBOX")
  // Suche aus dem neuen Postfach (?q=) übernehmen — sie läuft hier, über alle Ordner.
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "")
  // Suche zugeklappt = Lupe in Knopfgroesse; erst der Klick gibt ihr Breite.
  const [searchOpen, setSearchOpen] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState("")
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => clearTimeout(t)
  }, [search])
  const [category, setCategory] = useState("")
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [fullscreenId, setFullscreenId] = useState<number | null>(null)
  // Esc schliesst das Vollbild — der Tooltip versprach es, ein Handler fehlte.
  const fullscreenRef = useRef<number | null>(null)
  fullscreenRef.current = fullscreenId
  // Deep-Link vom Live-Banner: /emails?open=<id> waehlt die Mail direkt aus
  const [searchParams, setSearchParams] = useSearchParams()
  // Aus dem Review gekommen? Dann brauchts einen Weg zurueck — die URL-Params
  // werden gleich geleert, also vorher merken.
  const navigate = useNavigate()
  const [backTo, setBackTo] = useState<string | null>(null)
  useEffect(() => {
    const openId = Number(searchParams.get("open") || 0)
    if (!openId) return
    // `from` traegt entweder den kompletten Rueckweg (mit Filtern) oder das
    // alte Kuerzel "review".
    const from = searchParams.get("from") || ""
    if (from) setBackTo(from === "review" ? "/review?tab=projects" : from)
    setSelectedId(openId)
    // `restore_draft` MUSS ueberleben: es wird von useContactDraftRestore in
    // einem eigenen Effekt gelesen. Wer hier stumpf alles leert, loescht den
    // Entwurf weg, bevor ihn jemand sehen konnte (Ticket #9).
    const behalten = new URLSearchParams()
    const rd = searchParams.get("restore_draft")
    if (rd) behalten.set("restore_draft", rd)
    // 🔴 `tenant` MUSS ueberleben (25.08.2026). Wurde er hier mit
    // weggeraeumt, fiel `useTenantFilter` auf den zuletzt gewaehlten Betrieb
    // aus dem localStorage zurueck — und dann stand links die Liste des einen
    // Mandanten und rechts die geoeffnete Mail des anderen.
    const t = searchParams.get("tenant")
    if (t) behalten.set("tenant", t)
    setSearchParams(behalten, { replace: true })
  }, [searchParams, setSearchParams])
  /**
   * Liste und offene Mail auf demselben Mandanten halten — beide Richtungen.
   *
   * Die Regeln stehen in `useMandantWechsel`: die Liste zieht einer fremden
   * Mail nach (#35), und ein Wechsel durch den Nutzer schliesst die offene
   * Mail (#45). Beide Faelle sehen im Zustand gleich aus; unterscheidbar sind
   * sie nur daran, wer die Aenderung ausgeloest hat.
   */
  const angleichen = useMandantWechsel({
    tenant,
    setTenant,
    mailSchliessen: () => setSelectedId(null),
  })

  const [selected, setSelected] = useState<Set<number>>(new Set())
  // Zwei-Klick-Schutz fuers Bulk-Loeschen. Faellt zurueck, sobald sich die
  // Auswahl aendert — sonst loescht der zweite Klick eine andere Menge als die,
  // fuer die bestaetigt wurde.
  const [loeschBestaetigung, setLoeschBestaetigung] = useState(false)
  const [threadView, setThreadView] = useState(() => localStorage.getItem("emails-thread-view") !== "false")
  const [composeOpen, setComposeOpen] = useState(false)
  // Mail-Entwurf aus der Leiste unten rechts (#131) — `?restore_draft=<id>`
  // ohne `open`. Kontakt-Entwuerfe tragen dieselbe Kennung, deshalb nur
  // uebernehmen, wenn es wirklich ein Mail-Entwurf ist.
  const [composeDraft, setComposeDraft] = useState<MailDraft | null>(null)
  useEffect(() => {
    const rd = searchParams.get("restore_draft")
    if (!rd) return
    const d = getDraft(rd)
    if (d?.kind !== "mail") return
    setComposeDraft(d)
    setComposeOpen(true)
    const rest = new URLSearchParams(searchParams)
    rest.delete("restore_draft")
    setSearchParams(rest, { replace: true })
  }, [searchParams, setSearchParams])
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  /** Filter aus der Übersicht „was ist zu tun" (Key eines offenen Punkts). */
  const [punktFilter, setPunktFilter] = useState("")
  const qcRoot = useQueryClient()

  // User-Flag (rote Fahne) — markiert Mails die der User noch bearbeiten muss.
  const flagMutation = useMutation({
    mutationFn: async (args: { emailId: number; flagged: boolean; note?: string | null }) => {
      const r = await fetch(`/api/v1/emails/${args.emailId}/flag`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flagged: args.flagged, note: args.note ?? null }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    // #36: sofort umschalten, nicht erst nach dem Neuladen der ganzen Liste.
    onMutate: async (args) => {
      await qcRoot.cancelQueries({ queryKey: ["emails"] })
      const vorher = qcRoot.getQueriesData<EmailListe>({ queryKey: ["emails"] })
      qcRoot.setQueriesData<EmailListe>({ queryKey: ["emails"] },
        (d) => fahneUmschalten(d, args.emailId, args.flagged, args.note ?? null))
      return { vorher }
    },
    onError: (err, _args, ctx) => {
      for (const [key, d] of ctx?.vorher ?? []) qcRoot.setQueryData(key, d)
      toast("Markierung fehlgeschlagen: " + (err instanceof Error ? err.message : String(err)), "error")
    },
    onSettled: () => qcRoot.invalidateQueries({ queryKey: ["emails"] }),
  })

  // #35: „Termin war – erledigen" in der Zeile — derselbe Weg wie „Erledigt"
  // in der Leiste „Noch offen" (PATCH status=done).
  const terminErledigen = useMutation({
    mutationFn: async (emailId: number) => {
      const { error } = await api.PATCH("/api/v1/emails/{email_id}", {
        params: { path: { email_id: emailId } }, body: { status: "done" },
      })
      if (error) throw new Error(String(error))
    },
    onSuccess: () => {
      toast("Als erledigt markiert", "success")
      qcRoot.invalidateQueries({ queryKey: ["emails"] })
    },
    onError: () => toast("Konnte nicht als erledigt markieren", "error"),
  })

  const [flagModalEmail, setFlagModalEmail] = useState<EmailItem | null>(null)
  const toggleFlag = (e: EmailItem) => {
    if (e.flagged) {
      flagMutation.mutate({ emailId: e.id, flagged: false })
    } else {
      setFlagModalEmail(e)
    }
  }

  // Im Reiter „Gesendet" laesst das Backend filtern. Sonst kaemen die letzten
  // 200 Mails nach Datum — bei Sanitec fast nur Posteingang — und der Reiter
  // bliebe leer, obwohl 498 gesendete Mails vorliegen.
  const serverRichtung = folder === "Sent" ? "sent" : undefined
  const serverOrdner = folder === SPAM_ORDNER ? SPAM_ORDNER : undefined

  // „Ältere laden": wie viele Mails geholt werden. Jeder Wechsel von
  // Betrieb, Suche, Ordner oder Postfach faengt wieder bei MAILS_SCHRITT an.
  const [anzahl, setAnzahl] = useState(MAILS_SCHRITT)
  useEffect(() => { setAnzahl(MAILS_SCHRITT) }, [tenant, debouncedSearch, serverRichtung, serverOrdner, pf.wahl])

  // Reiter offen/meine/wartet holt die Arbeitsliste vom Server (Regel wie im
  // Review). Bei aktiver Suche gilt die Suche — über alle Ordner.
  const serverAnsicht = ansicht !== "alle" && !debouncedSearch ? ansicht : undefined
  const ansichtZahlen = useAnsichtZahlen(tenant, pf.wahl)
  // Eine gewählte Person gilt nur in „Meine"; Betriebswechsel setzt sie zurück.
  const serverPerson = serverAnsicht === "meine" && person ? person : undefined
  useEffect(() => { setPerson(null) }, [tenant])
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["emails", tenant, debouncedSearch, serverRichtung, pf.wahl, anzahl, serverAnsicht, serverOrdner, serverPerson],
    queryFn: () => fetchEmails(tenant, debouncedSearch || undefined, serverRichtung, pf.wahl || undefined, anzahl, serverAnsicht, serverOrdner, serverPerson),
    // Beim Nachladen die bisherige Liste stehen lassen, statt kurz „Lädt…".
    placeholderData: keepPreviousData,
    // Bei aktiver Suche kein Auto-Refresh — sonst springt der Cursor
    refetchInterval: debouncedSearch ? false : 60_000,
  })

  // Manueller IMAP-Sync — triggert echten Pull vom Mail-Server, dann DB-Refetch.
  // Ohne diesen Schritt zeigt der Refresh-Button nur was der Background-Loop
  // (alle ~30 Min) bereits geholt hat.
  const [syncing, setSyncing] = useState(false)
  const triggerImapSync = async () => {
    if (syncing) return
    setSyncing(true)
    try {
      const r = await fetch(`/api/v1/emails/sync?tenant=${encodeURIComponent(tenant || "")}`, {
        method: "POST", credentials: "include",
      })
      if (!r.ok) {
        toast(`IMAP-Sync HTTP ${r.status}`, "error")
      } else {
        const j = await r.json()
        const total = j?.total_new || 0
        if (total > 0) toast(`${total} neue Mails synchronisiert`, "success")
        // Stille bei 0 neuen — kein Spam
      }
    } catch (e) {
      toast(`Sync-Fehler: ${String(e)}`, "error")
    } finally {
      setSyncing(false)
      await refetch()
    }
  }

  // Auto-"gelesen" beim Klick auf eine ungelesene E-Mail
  const markRead = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await api.PATCH("/api/v1/emails/{email_id}", {
        params: { path: { email_id: id } },
        body: { status: "reviewed" },
      })
      if (error) throw new Error(String(error))
    },
    onSuccess: () => qcRoot.invalidateQueries({ queryKey: ["emails"] }),
  })

  const openEmail = (id: number) => {
    setSelectedId(id)
    const mail = (data?.items ?? []).find((m) => m.id === id)
    if (mail && mail.status === "new") markRead.mutate(id)
  }

  /**
   * Zeilen sofort aus der sichtbaren Liste nehmen (25.08.2026).
   *
   * 🔴 „Es muss wirklich schnell und fluessig gehen im Mail Programm." Der
   * Server antwortet inzwischen sofort — aber `invalidateQueries` laedt die
   * ganze Liste neu, und bis die da ist, stehen die Mails weiter da. Genau das
   * fuehlt sich langsam an, egal wie schnell die Anfrage war.
   *
   * Deshalb: den Cache direkt anfassen und die Zeilen entfernen. Die Anfrage
   * laeuft daneben; geht sie schief, holt `onError` den Stand zurueck.
   */
  const zeilenAusListeNehmen = (ids: number[]) => {
    const weg = new Set(ids)
    const vorher: [unknown, unknown][] = []
    qcRoot.setQueriesData({ queryKey: ["emails"] }, (alt: any) => {
      vorher.push([alt, null])
      if (!alt?.items) return alt
      return { ...alt, items: alt.items.filter((m: EmailItem) => !weg.has(m.id)) }
    })
    return vorher
  }

  const bulkAction = useMutation({
    onMutate: ({ action }: { action: string; folderTarget?: string }) => {
      // Nur wenn die Mails die Liste wirklich verlassen. „gelesen" laesst sie
      // stehen — dort waere das Wegnehmen falsch.
      if (action !== "move" && action !== "delete") return
      const ids = expandSelection(Array.from(selected))
      zeilenAusListeNehmen(ids)
      setSelected(new Set())
    },
    mutationFn: async ({ action, folderTarget }: { action: string; folderTarget?: string }) => {
      const ids = expandSelection(Array.from(selected))
      // 🔑 03.09.2026: Die Wege-Auswertung zeigte 35 EINZELNE Verschiebungen
      // und keinen einzigen Sammelvorgang — Marlene elfmal hintereinander in
      // derselben Sitzung. Der Schluss „das Sammeln wird nicht benutzt" war
      // aber nicht zu ziehen: gezaehlt wurde nur das Ziehen in der
      // Ordnerleiste (`FolderItems.tsx`), dieser Weg hier — Haekchen plus
      // Aktionsleiste, der naheliegendere — gar nicht. Erst mit beiden Zahlen
      // laesst sich sagen, ob der Sammelweg ungenutzt oder nur unauffindbar
      // ist.
      wegSchritt(ids.length > 1 ? `mails_gesammelt_${action}` : `mail_${action}`)
      const res = await fetch("/api/v1/emails/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids, action, folder: folderTarget || "" }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return { ...(await res.json()), angefragt: ids.length }
    },
    // 🔴 Nicht blind "erledigt" melden: das Backend sagt jetzt, wie viele Mails
    // wirklich umgestellt wurden und was auf dem Mailserver offen blieb. Genau
    // dieses Schweigen war der Grund, warum "gelesen/ungelesen bei mehreren"
    // als unzuverlaessig galt — es passierte teils nichts und niemand sagte es.
    onSuccess: (d: { geaendert?: number; imap_offen?: number; angefragt?: number;
                     fehler?: Array<{ grund?: string }> }) => {
      qcRoot.invalidateQueries({ queryKey: ["emails"] })
      setSelected(new Set())
      const angefragt = d.angefragt ?? 0
      const geaendert = d.geaendert ?? 0
      if (geaendert < angefragt) {
        toast(`${geaendert} von ${angefragt} Mails geändert — ${d.fehler?.[0]?.grund || "Rest fehlgeschlagen"}`, "error")
      } else if (d.imap_offen) {
        toast(`${geaendert} im Portal geändert, ${d.imap_offen} nicht im Postfach gespiegelt`, "error")
      }
    },
    onError: (e) => {
      // Zurueckholen, was optimistisch entfernt wurde — sonst fehlen Zeilen,
      // die es noch gibt.
      qcRoot.invalidateQueries({ queryKey: ["emails"] })
      toast(`Aktion fehlgeschlagen: ${String(e)}`, "error")
    },
  })
  // #196: „Als Spam" für eine Mehrfachauswahl — eine Mail nach der anderen über
  // dieselbe Route wie der Einzelknopf (Absender wird gemerkt, Ordner je Postfach).
  const bulkSpam = useMutation({
    mutationFn: async (ids: number[]) => {
      wegSchritt(ids.length > 1 ? "mails_gesammelt_spam" : "mail_spam")
      let geschafft = 0
      let erster = ""
      for (const id of ids) {
        try {
          const res = await fetch(`/api/v1/emails/${id}/spam`, { method: "POST", credentials: "include" })
          if (res.ok) { geschafft++; continue }
          const j = await res.json().catch(() => ({}))
          if (!erster) erster = j.detail || j.error || `HTTP ${res.status}`
        } catch (err) {
          if (!erster) erster = String(err)
        }
      }
      return { geschafft, angefragt: ids.length, fehler: erster }
    },
    onSuccess: ({ geschafft, angefragt, fehler }) => {
      qcRoot.invalidateQueries({ queryKey: ["emails"] })
      setSelected(new Set())
      setSelectedId(null)
      if (geschafft < angefragt) {
        toast(`${geschafft} von ${angefragt} Mails als Spam verschoben — ${fehler || "Rest fehlgeschlagen"}`, "error")
      } else {
        toast(`✓ ${geschafft === 1 ? "Mail" : `${geschafft} Mails`} als Spam verschoben — Absender werden künftig automatisch einsortiert`)
      }
    },
    onError: (err) => {
      qcRoot.invalidateQueries({ queryKey: ["emails"] })
      toast(`Spam fehlgeschlagen: ${String(err)}`, "error")
    },
  })
  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })

  // Auto-Select: E-Mails ohne Tenant-Filter macht keinen Sinn (Inboxen mischen
  // sich nicht sauber). Letzte Wahl aus localStorage oder ersten Tenant nehmen.
  useEffect(() => {
    if (tenant) {
      localStorage.setItem("emails-last-tenant", tenant)
      return
    }
    const items = tenantsDd.data?.items
    if (!items?.length) return
    const last = localStorage.getItem("emails-last-tenant")
    const pick = (last && items.find((t) => t.slug === last)?.slug) || items[0].slug
    if (pick) setTenant(pick)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, tenantsDd.data])

  // IMAP-Ordner aus dem Mailserver (M7 Parität)
  const imapFolders = useQuery({
    queryKey: ["imap-folders", tenant, pf.wahl],
    queryFn: async () => {
      if (!tenant) return []
      // Ein Postfach gewählt → dessen Ordner; „alle" → die aller Postfächer zusammen.
      const r = await fetch(`/api/v1/folders?tenant=${encodeURIComponent(tenant)}${postfachQuery(pf.wahl)}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      return Array.isArray(data) ? data as string[] : []
    },
    enabled: !!tenant,
    staleTime: 1000 * 60 * 5,
  })

  // Alle Mails für Tenant-Zähler im Dropdown (K4 Parität)
  const allEmailsForCount = useQuery({
    queryKey: ["emails", ""],
    queryFn: () => fetchEmails(""),
    staleTime: 1000 * 60,
    enabled: tenant !== "",
  })
  const tenantCounts = useMemo(() => {
    const source = tenant === "" ? (data?.items ?? []) : (allEmailsForCount.data?.items ?? [])
    const m: Record<string, number> = {}
    for (const e of source) {
      const t = e.tenant_slug || ""
      if (t) m[t] = (m[t] || 0) + 1
    }
    return m
  }, [data, allEmailsForCount.data, tenant])

  const all = data?.items ?? []
  // Ordnerleiste (Schritt H): IMAP-Ordner kommen vom Server in dessen
  // Reihenfolge — bei Sanitec sind das ueber 30 in gewachsener Unordnung.
  // Sortiert und ab 12 Stueck filterbar; deutsche Sortierung, damit
  // Umlaute dort stehen, wo man sie sucht.
  const sichtbareImapOrdner = useMemo(() => {
    const bekannt = ["INBOX", "SENT", "SENT ITEMS", "ARCHIVE", "ARCHIV", "TRASH",
                     "PAPIERKORB", "DRAFTS", "ENTWUERFE", "ENTWÜRFE", "JUNK", "SPAM"]
    return (imapFolders.data ?? [])
      .filter((f) => {
        const upper = f.toUpperCase()
        return !bekannt.includes(upper) && !upper.startsWith("[GMAIL]")
      })
      .sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" }))
  }, [imapFolders.data])
  const gefilterteImapOrdner = useMemo(() => {
    const q = folderFilter.trim().toLowerCase()
    return q ? sichtbareImapOrdner.filter((f) => f.toLowerCase().includes(q)) : sichtbareImapOrdner
  }, [sichtbareImapOrdner, folderFilter])

  // Die Zaehler sind eine Naeherung aus den geladenen Mails — sie waren es
  // immer. Neu ist nur: im Reiter „Gesendet" enthaelt die Liste bewusst nur
  // ausgehende Mails. Ohne die Merker stuenden in der Sidebar dann ueberall
  // Nullen, und es saehe aus, als waeren die Ordner leer.
  const letzteZaehler = useRef<Record<string, number>>({})
  const folderCounts = useMemo(() => {
    if (serverRichtung || serverOrdner) return letzteZaehler.current
    const m: Record<string, number> = {}
    for (const e of all) {
      const f = e.imap_folder || "INBOX"
      m[f] = (m[f] || 0) + 1
      if (istSpamOrdner(f)) m[SPAM_ORDNER] = (m[SPAM_ORDNER] || 0) + 1
    }
    letzteZaehler.current = m
    return m
  }, [all, serverRichtung, serverOrdner])
  const catCounts = useMemo(() => {
    const m: Record<string, number> = {}
    for (const e of all.filter((x) => folder === SPAM_ORDNER ? istSpamOrdner(x.imap_folder) : (x.imap_folder || "INBOX") === folder)) {
      if (e.category) m[e.category] = (m[e.category] || 0) + 1
    }
    // #193: feste Kategorien stehen immer als Chip da, auch bei Zaehler 0
    for (const k of FESTE_KATEGORIEN) if (!(k in m)) m[k] = 0
    return m
  }, [all, folder])
  // Sent-Mails liegen je nach Provider in unterschiedlichen Ordnern
  // (Gmail: "[Gmail]/Sent Mail", IONOS: "Gesendete Objekte", Outlook: "Sent Items").
  // Wenn User "Gesendet"-Pseudo-Ordner waehlt: alle mit email_direction=sent matchen.
  const isSentFolder = (imap: string) => {
    const s = (imap || "").toLowerCase()
    // Folder-Heuristik: sent/gesendet/Ausgang/geschickt/outbox/Ausgangsrechnungen.
    // Spiegelt _IMAP_SENT_FOLDERS aus core/state.py, damit User-spezifische Folder
    // ("05 Ausgangsrechnungen&-Angebote gesendet") auch ohne email_direction='sent'
    // im Ausgang erscheinen — defensive fuer alte Daten + Custom-Folder.
    return (
      s.includes("sent") || s.includes("gesendet") ||
      s.includes("ausgang") || s.includes("ausgehend") ||
      s.includes("geschickt") || s.includes("outbox")
    )
  }
  const isSearching = !!debouncedSearch
  const filtered = useMemo(() => {
    let f = all
    // Bei aktiver Suche ueberspringen wir Folder/Direction — globale Suche
    // ueber ALLE Ordner und Richtungen, damit eine alte Rechnung in
    // "Archiv" oder im "Gesendet"-Ordner auch wirklich gefunden wird.
    if (!isSearching && !serverAnsicht) {
      // Die Ordnerwahl legt die Richtung bereits fest: "Gesendet" zeigt
      // ausgehende, jeder andere Ordner eingehende Mails. Ein zusaetzlicher
      // Richtungsfilter konnte deshalb nie etwas einschraenken, sondern die
      // Liste nur leeren — "Gesendet" + Standard "Eingang" ergab null Treffer,
      // man musste unten nochmal "Ausgang" klicken.
      if (folder === "Sent") {
        f = f.filter((e) => e.email_direction === "sent" || isSentFolder(e.imap_folder || ""))
      } else if (folder === SPAM_ORDNER) {
        f = f.filter((e) => istSpamOrdner(e.imap_folder) && e.email_direction !== "sent")
      } else {
        f = f.filter((e) => (e.imap_folder || "INBOX") === folder && e.email_direction !== "sent")
      }
    }
    if (category) f = f.filter((e) => e.category === category)
    if (flaggedOnly) f = f.filter((e) => e.flagged)
    if (punktFilter && serverAnsicht) f = f.filter((e) => (e.offene_punkte || []).some((p) => p.key === punktFilter))
    // Wenn der debounce eingeholt ist, hat das Backend bereits volltext-gefiltert
    // (subject + body + sender + recipient + drafts). Dann KEIN Client-Filter
    // mehr aufsetzen, sonst wuerden Body-Treffer wieder rausfallen.
    // Solange der User noch tippt (search != debouncedSearch), nutzen wir den
    // Client-Filter auf subject/sender fuer Sofort-Feedback gegen das alte
    // Result-Set.
    if (search && search.trim() !== debouncedSearch) {
      const q = search.toLowerCase()
      f = f.filter((e) =>
        (e.subject || "").toLowerCase().includes(q) ||
        (e.sender || "").toLowerCase().includes(q) ||
        (e.sender_email || "").toLowerCase().includes(q),
      )
    }
    return f
  }, [all, folder, category, search, debouncedSearch, flaggedOnly, serverAnsicht, punktFilter])

  const flaggedCount = useMemo(() => all.filter((e) => e.flagged).length, [all])
  // Übersicht „was ist zu tun" — aus den geladenen Mails, gleiche Punkte wie
  // in den Zeilen. Reihenfolge: dringend zuerst, dann nach Häufigkeit.
  const offenNachArt = useMemo(() => {
    const z = new Map<string, [string, number]>()
    for (const e of all) for (const p of e.offene_punkte || []) {
      const alt = z.get(p.key)
      const name = p.key === "danach" ? "danach etwas passiert"
        : p.key === "ausgang" ? "schon bearbeitet"
        : p.key === "termin_war" ? "Termin war"
        : p.label.replace(/^\d+ To-dos offen$/, "To-dos offen")
      z.set(p.key, [alt?.[0] || name, (alt?.[1] || 0) + 1])
    }
    return [...z.entries()].map(([k, [l, n]]) => [k, l, n] as [string, string, number])
      .sort((a, b) => (a[0] === "wiedervorlage" ? -1 : b[0] === "wiedervorlage" ? 1 : b[2] - a[2]))
  }, [all])

  // Thread-Gruppierung: nur neueste Mail pro thread_id anzeigen, + Badge mit Count
  const threadHeadsWithCounts = useMemo(() => {
    if (!threadView) return filtered.map((e) => ({ email: e, threadCount: 1, threadMails: [e] }))
    const threadMap = new Map<string, EmailItem[]>()
    for (const e of filtered) {
      const key = e.thread_id || `single:${e.id}`
      const arr = threadMap.get(key) || []
      arr.push(e)
      threadMap.set(key, arr)
    }
    // Pro Thread: sortiere nach received_at DESC, head = erstes Item (neueste)
    const heads: Array<{ email: EmailItem; threadCount: number; threadMails: EmailItem[] }> = []
    for (const [, mails] of threadMap) {
      const sorted = [...mails].sort((a, b) => {
        const da = new Date(a.received_at || "").getTime()
        const db = new Date(b.received_at || "").getTime()
        return db - da
      })
      heads.push({ email: sorted[0], threadCount: sorted.length, threadMails: sorted })
    }
    return heads.sort((a, b) => {
      const da = new Date(a.email.received_at || "").getTime()
      const db = new Date(b.email.received_at || "").getTime()
      return db - da
    })
  }, [filtered, threadView])

  // Eine Zeile in der Thread-Ansicht ist eine UNTERHALTUNG, keine einzelne
  // Mail. Wer sie ankreuzt und "gelesen" klickt, meint die ganze Unterhaltung —
  // vorher wanderte nur die neueste Mail des Threads durch, die aelteren blieben
  // ungelesen. Das sah aus wie "die Mehrfachauswahl klappt nicht zuverlaessig".
  // Nur Mails aus der aktuellen Ansicht: was in einem anderen Ordner liegt,
  // wird nicht mitgeloescht.
  const expandSelection = (ids: number[]): number[] =>
    expandThreadSelection(ids, threadHeadsWithCounts)

  // Tastenkürzel der Liste (Variante A): J/K nächste/vorige Mail. R/E/N/V
  // hängen an der geöffneten Mail (EmailDetailPane).
  const tastenRef = useRef<{ ids: number[]; sel: number | null; oeffnen: (id: number) => void }>({ ids: [], sel: null, oeffnen: () => {} })
  tastenRef.current = { ids: threadHeadsWithCounts.map((h) => h.email.id), sel: selectedId, oeffnen: (id) => openEmail(id) }
  useEffect(() => {
    const aufTaste = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && fullscreenRef.current) { setFullscreenId(null); return }
      if (!tasteZaehlt(ev)) return
      const k = ev.key.toLowerCase()
      if (k !== "j" && k !== "k") return
      const { ids, sel, oeffnen } = tastenRef.current
      if (!ids.length) return
      const i = sel == null ? -1 : ids.indexOf(sel)
      const ziel = k === "j" ? ids[Math.min(ids.length - 1, i + 1)] : ids[Math.max(0, i <= 0 ? 0 : i - 1)]
      if (ziel != null && ziel !== sel) {
        ev.preventDefault()
        oeffnen(ziel)
        document.querySelector(`[data-mail-zeile="${ziel}"]`)?.scrollIntoView({ block: "nearest" })
      }
    }
    window.addEventListener("keydown", aufTaste)
    return () => window.removeEventListener("keydown", aufTaste)
  }, [])

  const toggleThreadView = () => {
    const newVal = !threadView
    setThreadView(newVal)
    localStorage.setItem("emails-thread-view", String(newVal))
  }

  const folders = [
    { id: "INBOX", name: "Posteingang", icon: <Inbox className="w-[14px] h-[14px]" /> },
    { id: "Sent", name: "Gesendet", icon: <Send className="w-[14px] h-[14px]" /> },
    { id: "Archive", name: "Archiv", icon: <CheckCircle className="w-[14px] h-[14px]" /> },
    { id: "Trash", name: "Papierkorb", icon: <Trash2 className="w-[14px] h-[14px]" /> },
    // Spam und Werbung (16.10.2026): der Spam-/Junk-Ordner jedes Postfachs.
    // Hier geht man durch, was automatisch einsortiert wurde, und holt mit
    // „Kein Spam" zurück, was doch echt war.
    { id: SPAM_ORDNER, name: "Spam", icon: <ShieldAlert className="w-[14px] h-[14px]" />, keinAblageziel: true },
  ]

  // U2 Pane-Resize: Breiten per localStorage persistent, aber begrenzt damit
  // der Detail-Pane immer sichtbar bleibt (auch wenn Viewport schrumpft).
  const [foldersW, setFoldersW] = useState(() => {
    const stored = Number(localStorage.getItem("emails-folders-w") || "220")
    return Math.min(stored, Math.max(160, window.innerWidth * 0.25))
  })
  // Wunschbreite des Nutzers (per Ziehen gesetzt). Wird nie beschnitten
  // gespeichert — nur beim Anzeigen begrenzt, damit sie zurueckkommt, sobald
  // wieder Platz da ist.
  const [listWunsch, setListWunsch] = useState(
    () => Number(localStorage.getItem("emails-list-w") || "420"))
  const [listW, setListW] = useState(listWunsch)
  // Wieviel Platz die Liste bekommen darf.
  //
  // 🔴 Vorher stand hier `vw - 240 - foldersW - 420 - 16` mit 240 fuer die
  // Hauptnavigation. Die kann inzwischen 64 px schmal oder ganz weg sein, und
  // die Ordnerspalte klappt sich bei schmalen Fenstern selbst ein — die
  // Rechnung verschenkte dann bis zu 396 px an die Detailspalte, auch wenn
  // dort nur „Klick auf eine E-Mail" stand. Gemessen am 23.08.2026: Liste
  // 368 px, leere Vorschau 818 px.
  //
  // Jetzt wird die tatsaechlich verfuegbare Breite gemessen statt geraten.
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const DETAIL_MIN = 420
  useEffect(() => {
    const anpassen = () => {
      const verfuegbar = bodyRef.current?.clientWidth ?? window.innerWidth
      const maxList = Math.max(300, verfuegbar - DETAIL_MIN - 16)
      // Nach oben begrenzen, nach unten aber auch wieder wachsen lassen:
      // klappt die Ordnerspalte zu, soll die Liste den Platz bekommen.
      setListW(Math.min(listWunsch, maxList))
    }
    anpassen()
    window.addEventListener("resize", anpassen)
    return () => window.removeEventListener("resize", anpassen)
  }, [foldersW, listWunsch, ordnerEingeklappt])
  // Refs auf die beiden resizeable Panes + Last-Width-Tracker.
  // Waehrend dem Drag schreiben wir die Breite DIREKT ins DOM (kein React-Rerender),
  // und comitten den finalen Wert erst bei mouseup in den State. Das macht das
  // Ziehen fluessig auch bei grossem Detail-Pane (der sonst bei jedem Mousemove
  // rerendern wuerde).
  const foldersRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const dragLastW = useRef(0)
  const startResize = (pane: "folders" | "list") => (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = pane === "folders" ? foldersW : listW
    dragLastW.current = startW
    const target = pane === "folders" ? foldersRef.current : listRef.current
    const handle = e.currentTarget
    const pid = e.pointerId
    // Threshold: Drag erst ab 3px Bewegung "echt" starten — verhindert
    // ungewollte Drags bei minimalem Maus-/Trackpad-Wackeln, die in einem
    // klebenden Cursor enden weil kein pointerup mehr kommt.
    let activated = false
    try { handle.setPointerCapture(pid) } catch { /* no-op */ }
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX
      if (!activated) {
        if (Math.abs(dx) < 3) return
        activated = true
        document.body.style.cursor = "col-resize"
        document.body.style.userSelect = "none"
      }
      const vw = window.innerWidth
      const sidebarW = 240, detailMin = 420, gutter = 16
      const otherPaneW = pane === "folders" ? listW : foldersW
      const maxForThisPane = Math.max(160, vw - sidebarW - otherPaneW - detailMin - gutter)
      const next = Math.max(160, Math.min(maxForThisPane, startW + dx))
      dragLastW.current = next
      if (target) {
        target.style.width = `${next}px`
        target.style.minWidth = `${next}px`
      }
    }
    const cleanup = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", cleanup)
      window.removeEventListener("pointercancel", cleanup)
      window.removeEventListener("blur", cleanup)
      window.removeEventListener("wheel", cleanup, true)
      document.removeEventListener("scroll", cleanup, true)
      try { handle.releasePointerCapture(pid) } catch { /* no-op */ }
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      // Nur persisten wenn der Drag tatsaechlich aktiv war (mehr als 3px).
      // Sonst war es nur ein Click — Width unveraendert lassen.
      if (activated) {
        const finalW = dragLastW.current
        if (pane === "folders") setFoldersW(finalW)
        // Beim Ziehen wird die WUNSCH-Breite gesetzt; der Effekt darueber
        // begrenzt sie dann auf das, was gerade hineinpasst.
        else { setListWunsch(finalW); setListW(finalW) }
        localStorage.setItem(pane === "folders" ? "emails-folders-w" : "emails-list-w", String(finalW))
      }
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", cleanup)
    window.addEventListener("pointercancel", cleanup)
    window.addEventListener("blur", cleanup)
    // Wheel/Scroll als Notbremse: wenn der User waehrend eines (vermeintlichen)
    // Drags scrollt, wird kein pointerup ausgeloest und der Cursor klebt.
    // Capture-Phase damit wir's vor dem Scroll-Handler bekommen.
    window.addEventListener("wheel", cleanup, { passive: true, capture: true })
    document.addEventListener("scroll", cleanup, { passive: true, capture: true })
  }
  // localStorage-Persist passiert in startResize onUp (damit wir kein State-Rerender brauchen)
  // Width direkt ueber DOM-Ref setzen (nicht als inline-Style-Prop), damit Re-Renders
  // des Parents (z.B. durch Query-Refetches alle 60s) die waehrend-Drag-DOM-Werte
  // nicht mit alten State-Werten ueberschreiben.
  useLayoutEffect(() => {
    if (foldersRef.current) {
      foldersRef.current.style.width = `${foldersW}px`
      foldersRef.current.style.minWidth = `${foldersW}px`
    }
  }, [foldersW])
  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.style.width = `${listW}px`
    }
  }, [listW])

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div ref={bodyRef} className="ev2-body" style={{ flex: 1 }}>
        {/* Ordnerleiste — einklappbar.
            🔴 Auf einem Laptop ohne zweiten Bildschirm teilen sich Ordner,
            Mailliste und Detailspalte rund 1280 px. Die Ordner braucht man
            aber nur beim Wechseln, nicht beim Lesen. Eingeklappt bleibt ein
            schmaler Streifen zum Wiederaufklappen stehen; der Zustand haelt
            ueber Neuladen, wie bei der Hauptnavigation. */}
        {ordnerEingeklappt && ordnerMenue && (
          <div className="ev2-ordner-schleier" onClick={() => setOrdnerMenue(false)} aria-hidden />
        )}
        <div
          ref={foldersRef}
          className={ordnerEingeklappt && ordnerMenue ? "ev2-folders ev2-folders-menue" : "ev2-folders"}
          hidden={ordnerEingeklappt && !ordnerMenue}
        >
          <div className="ev2-folders-head">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
              {ordnerEingeklappt ? (
                <button
                  className="ev2-folders-toggle"
                  onClick={() => { setOrdnerEingeklappt(false); setOrdnerMenue(false); localStorage.setItem("ev2_ordner", "auf") }}
                  title="Ordner als Spalte anheften"
                  style={{ width: "auto", padding: "0 8px", fontSize: 12 }}
                >
                  Als Spalte anheften
                </button>
              ) : (
                <button
                  className="ev2-folders-toggle"
                  onClick={() => { setOrdnerEingeklappt(true); localStorage.setItem("ev2_ordner", "zu") }}
                  title="Ordner ausblenden"
                  aria-label="Ordner ausblenden"
                  aria-expanded
                >
                  <ChevronLeft style={{ width: 14, height: 14 }} />
                </button>
              )}
            </div>
            <select value={tenant} onChange={(e) => setTenant(e.target.value)}>
              <option value="">Alle Tenants</option>
              {tenantsDd.data?.items?.map((t) => {
                const cnt = tenantCounts[t.slug] || 0
                return (
                  <option key={t.slug} value={t.slug}>
                    {t.name}{cnt > 0 ? ` (${cnt})` : ""}
                  </option>
                )
              })}
            </select>
            {pf.weitere.length > 0 && (
              <select
                value={pf.wahl}
                data-testid="postfach-wahl"
                title="Welches Postfach anzeigen? Ordner, Mails und neue Mails richten sich danach."
                onChange={(e) => { pf.setWahl(e.target.value); setFolder("INBOX"); setCategory(""); setSelectedId(null) }}
              >
                <option value="">Alle Postfächer</option>
                {pf.haupt && <option value={HAUPT}>{postfachName(pf.haupt)}</option>}
                {pf.weitere.map((p) => <option key={p.adresse} value={p.adresse}>{postfachName(p)}</option>)}
              </select>
            )}
            <button
              className="ev2-sync-btn"
              onClick={triggerImapSync}
              disabled={syncing || isFetching}
              title="Holt neue Mails direkt vom IMAP-Server"
            >
              {syncing ? "Synchronisiere..." : isFetching ? "Lade..." : "Sync"}
            </button>
          </div>
          <div className="ev2-folder-list">
            {folders.map((f) => (
              <StandardFolderItem
                key={f.id}
                folderDef={f}
                active={folder === f.id}
                count={folderCounts[f.id] || 0}
                tenant={tenant}
                onClick={() => { setFolder(f.id); setCategory(""); setSelectedId(null); setAnsicht("alle"); setOrdnerMenue(false) }}
                onVerschoben={() => { setSelected(new Set()); setOrdnerMenue(false) }}
              />
            ))}
            {tenant && (
              <>
                <div className="ev2-divider" />
                <div className="ev2-section-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 12 }}>
                  <span>IMAP-Ordner</span>
                  <button
                    onClick={async () => {
                      const name = prompt("Name des neuen Ordners:")
                      if (!name?.trim()) return
                      try {
                        const r = await fetch("/api/v1/folders", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ tenant, name: name.trim(), postfach: pf.wahl || null }),
                        })
                        const d = await r.json()
                        if (r.ok) {
                          toast("✓ Ordner '" + name.trim() + "' erstellt")
                          qcRoot.invalidateQueries({ queryKey: ["imap-folders", tenant] })
                        } else toast(d.error || "Fehler", "error")
                      } catch (e) {
                        toast("Fehler: " + (e instanceof Error ? e.message : String(e)), "error")
                      }
                    }}
                    title="Neuer Ordner"
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: "var(--accent-color)", fontSize: 14, padding: 0,
                    }}
                  >
                    +
                  </button>
                </div>
                {sichtbareImapOrdner.length > 12 && (
                  <input
                    className="ev2-folder-filter"
                    value={folderFilter}
                    onChange={(ev) => setFolderFilter(ev.target.value)}
                    placeholder={`${sichtbareImapOrdner.length} Ordner filtern…`}
                    aria-label="Ordner filtern"
                  />
                )}
                {gefilterteImapOrdner.length === 0 && folderFilter && (
                  <div className="ev2-folder-empty">Kein Ordner passt zu „{folderFilter}"</div>
                )}
                {gefilterteImapOrdner
                  .map((f) => (
                    <FolderItem
                      key={f}
                      name={f}
                      active={folder === f}
                      count={folderCounts[f] || 0}
                      tenant={tenant}
                      postfach={pf.wahl}
                      onClick={() => { setFolder(f); setCategory(""); setSelectedId(null); setAnsicht("alle"); setOrdnerMenue(false) }}
                      onAction={() => qcRoot.invalidateQueries({ queryKey: ["imap-folders", tenant] })}
                      onVerschoben={() => { setSelected(new Set()); setOrdnerMenue(false) }}
                    />
                  ))}
                {/* #55: der echte Spam-Ordner, live vom Server — nicht gesynct */}
                <div
                  className="ev2-folder-item"
                  style={{ cursor: "pointer", opacity: 0.85 }}
                  onClick={() => setSpamOffen(true)}
                  title="Alles, was im Spam-/Junk-Ordner auf dem Mailserver liegt — auch, was der Spamfilter des Anbieters selbst einsortiert hat"
                >
                  🛡 Spam auf dem Server (live)
                </div>
              </>
            )}
            {Object.keys(catCounts).length > 0 && (
              <>
                <div className="ev2-divider" />
                <div className="ev2-section-label">Kategorien</div>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {Object.entries(catCounts).sort().map(([cat, count]) => (
                    <span
                      key={cat}
                      className={cn("ev2-cat-chip", category === cat && "active")}
                      onClick={() => setCategory(cat === category ? "" : cat)}
                    >
                      {cat} <span className="ev2-cat-count">{count}</span>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div
          hidden={ordnerEingeklappt}
          onPointerDown={startResize("folders")}
          title="Breite anpassen"
          style={{
            width: 4, cursor: "col-resize", background: "transparent",
            borderLeft: "1px solid var(--border-color)",
            transition: "background 0.1s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-color)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        />

        {/* Liste */}
        <div ref={listRef} className="ev2-list" style={{ flex: "none" }}>
          <div className="ev2-listenkopf">
            <button type="button" className="ev2-ordnerwahl" aria-expanded={ordnerMenue}
              onClick={() => { if (ordnerEingeklappt) setOrdnerMenue(!ordnerMenue) }}
              title={ordnerEingeklappt ? "Ordner wählen – Mails lassen sich auch hierher ziehen" : undefined}>
              {ansicht === "alle" || isSearching
                ? (folders.find((f) => f.id === folder)?.name || folder)
                : ansicht === "offen" ? "Offen" : ansicht === "meine" ? (serverPerson ? `Offen bei ${anzeigeName(serverPerson)}` : "Meine offenen") : ansicht === "kunde_wartet" ? "Kunde wartet" : "Wartet auf Kunde"}
              {ordnerEingeklappt && <ChevronDown className="w-4 h-4" aria-hidden />}
            </button>
            <span style={{ flex: 1 }} />
            {/* #196: bei eingeklappter Ordnerspalte ist „Spam" sonst nur über das
                Ordnermenü erreichbar — hier ein direkter Weg. */}
            {ordnerEingeklappt && (
              <button type="button" data-testid="spam-ordner-kurzweg"
                className={folder === SPAM_ORDNER ? "ev2-tb-btn active" : "ev2-tb-btn"}
                title="Spam-Ordner öffnen: hier prüfen, was automatisch einsortiert wurde"
                onClick={() => { setFolder(SPAM_ORDNER); setCategory(""); setSelectedId(null); setAnsicht("alle"); setOrdnerMenue(false) }}>
                <ShieldAlert className="w-3 h-3" /> Spam
              </button>
            )}
            <button type="button" onClick={() => setComposeOpen(true)} className="ev2-tb-btn primary">
              <Pencil className="w-3 h-3" /> Neu
            </button>
          </div>
          {<AnsichtReiter
            ansicht={ansicht}
            setAnsicht={(a) => { setAnsicht(a); setSelectedId(null); setSelected(new Set()); setPunktFilter("") }}
            zahlen={ansichtZahlen.data ? { offen: ansichtZahlen.data.offen, meine: ansichtZahlen.data.meine, wartet: ansichtZahlen.data.wartet, kunde_wartet: ansichtZahlen.data.kunde_wartet } : undefined}
            personen={ansichtZahlen.data?.personen}
            person={serverPerson}
            setPerson={(name) => {
              setPerson(name)
              // Die Person gilt in „Meine"; ohne Person geht es dorthin zurück, wo man war.
              if (name) setAnsichtRoh("meine")
              setSelectedId(null); setSelected(new Set()); setPunktFilter("")
            }}
          />}
          {/* Auf einen Blick (14.10.2026): was in dieser Ansicht zu tun ist,
              gezählt nach Art. Ein Klick filtert darauf. */}
          {serverAnsicht && serverAnsicht !== "wartet" && offenNachArt.length > 0 && (
            <div className="ev2-offen-uebersicht" aria-label="Was zu tun ist">
              {offenNachArt.map(([key, labelText, n]) => (
                <button key={key} type="button" aria-pressed={punktFilter === key}
                  className={punktFilter === key ? "ev2-uebersicht-chip aktiv" : "ev2-uebersicht-chip"}
                  onClick={() => setPunktFilter(punktFilter === key ? "" : key)}>
                  <b>{n}</b> {labelText}
                </button>
              ))}
            </div>
          )}
          <div className="ev2-toolbar">
            <button
              className="ev2-tb-btn icon"
              onClick={triggerImapSync}
              disabled={syncing || isFetching}
              title="IMAP-Sync: holt neue Mails vom Mail-Server"
            >
              <RefreshCw className={cn("w-[14px] h-[14px]", (syncing || isFetching) && "animate-spin")} />
            </button>
            {/* #146 (04.10.2026): Die Lupe stand als LETZTER Knopf ganz rechts.
                Die Leiste ist breiter (~540 px) als die Liste (Standard 420,
                oft 368 px), und `.ev2-list` schneidet mit `overflow: hidden`
                ab — also verschwand genau die Lupe. Jetzt steht sie vorn,
                direkt nach dem Sync, und die Leiste bricht um statt
                abzuschneiden. */}
            <ToolbarSearch
              value={search}
              onChange={setSearch}
              open={searchOpen}
              setOpen={setSearchOpen}
              isSearching={isSearching}
            />
            {backTo && (
              <button
                type="button"
                onClick={() => navigate(backTo)}
                className="ev2-tb-btn"
                title="Zurueck zur Projekt-Liste im Review"
              >
                <ArrowLeft className="w-3 h-3" /> Review
              </button>
            )}
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {filtered.length} E-Mails
            </span>
            <div style={{
              marginLeft: "auto", display: "flex", alignItems: "center", gap: 6,
              flexWrap: "wrap", justifyContent: "flex-end",
            }}>
              {/* Dichte (Schritt H) — kompakt zeigt rund doppelt so viele
                  Mails auf einem Schirm. Merkt sich der Browser. */}
              <div className="ev2-density" role="group" aria-label="Listendichte">
                {DENSITIES.map((d) => (
                  <button key={d} type="button"
                    className={cn(d === density && "active")}
                    onClick={() => setDensity(d)}
                    title={{ compact: "Kompakt", normal: "Normal", cozy: "Luftig" }[d]}
                    aria-pressed={d === density}>
                    <span style={{
                      display: "block", width: 12,
                      borderTop: "2px solid currentColor",
                      borderBottom: "2px solid currentColor",
                      height: { compact: 5, normal: 8, cozy: 11 }[d],
                    }} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={toggleThreadView}
                className={cn("ev2-tb-btn", !threadView && "on")}
                title={threadView ? "Zur chronologischen Ansicht wechseln" : "Zur Thread-Ansicht wechseln"}
              >
                ☰ Chronologisch
              </button>
              {onNeuesPostfach && (
                <button
                  type="button"
                  onClick={onNeuesPostfach}
                  className="ev2-tb-btn"
                  data-testid="ansicht-schalter"
                  title="Zum neuen Postfach: Ein Klick, Braucht dich, automatisch erledigt (gilt nur für dich)"
                >
                  Neues Postfach
                </button>
              )}
              <button
                type="button"
                onClick={() => setFlaggedOnly(!flaggedOnly)}
                title="Nur markierte Mails anzeigen (rote Fahne)"
                className={cn("ev2-tb-btn", flaggedOnly && "flag-on")}
              >
                <Flag size={11} fill={flaggedOnly ? "#dc2626" : "transparent"} color={flaggedOnly ? "#dc2626" : "currentColor"} strokeWidth={flaggedOnly ? 0 : 1.8} />
                Markiert
                {flaggedCount > 0 && <span className="ev2-tb-count">{flaggedCount}</span>}
              </button>
            </div>
          </div>
          {selected.size > 0 && (
            <div style={{
              padding: "8px 16px", background: "var(--accent-light, rgba(99,102,241,.08))",
              borderBottom: "1px solid var(--border-color)",
              display: "flex", alignItems: "center", gap: 8, fontSize: 12.5,
            }}>
              <BulkToolbar
                count={selected.size}
                pending={bulkAction.isPending || bulkSpam.isPending}
                loeschBestaetigung={loeschBestaetigung}
                setLoeschBestaetigung={setLoeschBestaetigung}
                onClear={() => { setSelected(new Set()); setLoeschBestaetigung(false) }}
                onAction={(action) => bulkAction.mutate({ action })}
                onSpam={folder === SPAM_ORDNER || folder === "Sent" ? undefined
                  : () => bulkSpam.mutate(expandSelection(Array.from(selected)))}
              />
            </div>
          )}
          <div style={{
            padding: "6px 16px", borderBottom: "1px solid var(--border-color)",
            display: "flex", alignItems: "center", gap: 12, fontSize: 12,
          }}>
            {/* 🔴 Bei aktiver Suche wird der Ordnerfilter bewusst uebersprungen
                (siehe `filtered`). Links bleibt aber „Posteingang" markiert —
                daraus wurde die Meldung „Warum ist denn die Antwort jetzt im
                Posteingang?" (04.09.2026, #74). Also hier sagen, was man sieht. */}
            <span className="text-muted-foreground ml-auto">
              {isSearching
                ? `${threadHeadsWithCounts.length} Treffer in allen Ordnern`
                : `${threadHeadsWithCounts.length} Einträge`}
            </span>
          </div>
          <div className={cn("ev2-email-rows", densityClass(density))}>
            {isLoading ? (
              <Loading label="Lädt E-Mails..." />
            ) : threadHeadsWithCounts.length === 0 ? (
              <div className="ev2-empty">
                {serverAnsicht === "offen" ? "Nichts offen – schön aufgeräumt."
                  : serverAnsicht === "meine" ? (serverPerson ? `Bei ${anzeigeName(serverPerson)} ist gerade nichts offen.` : "Für dich ist gerade nichts offen.")
                  : serverAnsicht === "wartet" ? "Gerade wartet nichts auf eine Antwort."
                  : serverAnsicht === "kunde_wartet" ? "Kein Kunde wartet länger als einen Werktag – schön."
                  : `Keine E-Mails in ${folders.find((f) => f.id === folder)?.name || folder}.`}
              </div>
            ) : (
              groupByDay(threadHeadsWithCounts).map(([day, heads]) => (
                <div key={day}>
                  <div className="ev2-day-header">{day}</div>
                  {heads.map(({ email: e, threadCount }) => (
                    <EmailRow
                      key={e.id}
                      email={e}
                      // Nur im Suchergebnis: sonst steht in jeder Zeile der
                      // Ordner, in dem man ohnehin gerade ist.
                      herkunftZeigen={isSearching}
                      threadCount={threadCount > 1 ? threadCount : undefined}
                      active={selectedId === e.id}
                      selected={selected.has(e.id)}
                      selectedIds={expandSelection(Array.from(selected))}
                      onSelect={(checked) => {
                        const n = new Set(selected)
                        if (checked) n.add(e.id); else n.delete(e.id)
                        setSelected(n)
                        setLoeschBestaetigung(false)
                      }}
                      onClick={() => openEmail(e.id)}
                      onDoubleClick={() => { openEmail(e.id); setFullscreenId(e.id) }}
                      onFlagToggle={() => toggleFlag(e)}
                      onTerminErledigen={() => terminErledigen.mutate(e.id)}
                      onSpam={folder === SPAM_ORDNER || e.email_direction === "sent" ? undefined
                        : () => bulkSpam.mutate([e.id])}
                      onZiehen={(aktiv) => { if (ordnerEingeklappt) setOrdnerMenue(aktiv) }}
                    />
                  ))}
                </div>
              ))
            )}
            {/* Die Liste holt nur die neuesten `anzahl` Mails; ob es ältere
                gibt, sagt das Backend (`mehr`). */}
            {!isSearching && !isLoading && data?.mehr && anzahl < MAILS_MAX && (
              <div style={{ padding: "12px 0", textAlign: "center" }}>
                <button
                  type="button"
                  className="pp-btn pp-btn-sm"
                  disabled={isFetching}
                  onClick={() => setAnzahl((n) => Math.min(MAILS_MAX, n + MAILS_SCHRITT))}
                >
                  {isFetching ? "Lädt…" : "Ältere E-Mails laden"}
                </button>
              </div>
            )}
            {/* DSGVO (02.10.2026): Mails älter als 90 Tage haben im Portal keinen
                Inhalt mehr — die normale Suche findet sie nicht. Darunter sucht
                deshalb das Postfach selbst, live und ohne zu speichern. */}
            {isSearching && search.trim() === debouncedSearch && (
              <PostfachTreffer tenant={tenant} postfach={pf.wahl || undefined} q={debouncedSearch} />
            )}
          </div>
          {<div className="ev2-kuerzel" aria-label="Tastenkürzel">
            <span><kbd>J</kbd><kbd>K</kbd> blättern</span>
            <span><kbd>R</kbd> antworten</span>
            <span><kbd>E</kbd> erledigt</span>
            <span><kbd>N</kbd> Notiz</span>
            <span><kbd>V</kbd> verschieben</span>
          </div>}
        </div>

        <div
          onPointerDown={startResize("list")}
          title="Breite anpassen"
          style={{
            width: 4, cursor: "col-resize", background: "transparent",
            borderLeft: "1px solid var(--border-color)",
            transition: "background 0.1s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-color)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        />

        {/* Detail-Pane rechts */}
        <div className="ev2-detail">
          {selectedId ? (
            // KRITISCH: key={selectedId} erzwingt frisches Re-Mount beim
            // Email-Switch. Ohne den key wird die Komponente wiederverwendet,
            // useState in ReplyForm/ForwardForm laeuft nur EINMAL → Subject
            // bleibt vom vorherigen Email haengen → User sendet Reply mit
            // FALSCHEM Betreff (Bug 08.05.2026, Familie-Beispiel-Storno mit
            // "Re: Angebot 21254582..."-Subject).
            <EmailDetailPane
              key={selectedId}
              emailId={selectedId}
              onClose={() => setSelectedId(null)}
              allEmails={all}
              onOpenEmail={openEmail}
              onTenantErkannt={angleichen}
            />
          ) : (
            <div className="ev2-detail-empty">
              <div style={{ textAlign: "center" }}>
                <MailIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <div>Klick auf eine E-Mail um sie rechts zu öffnen. Doppelklick öffnet Vollbild.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen-Overlay bei Doppelklick oder Vollbild-Button */}
      {fullscreenId !== null && (
        <div
          className="pp-overlay"
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.5)", padding: 24,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setFullscreenId(null) }}
        >
          <div
            style={{
              background: "var(--bg-card)", borderRadius: 12,
              width: "95vw", height: "92vh", maxWidth: 1400,
              boxShadow: "0 20px 60px rgba(0,0,0,.35)",
              display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 16px", borderBottom: "1px solid var(--border-color)",
              }}
            >
              <strong style={{ fontSize: 13 }}>E-Mail (Vollbild)</strong>
              <button
                className="pp-btn pp-btn-sm"
                onClick={() => setFullscreenId(null)}
                title="Schließen (Esc)"
              >
                ✕ Schließen
              </button>
            </div>
            <div style={{ flex: 1, overflow: "auto" }}>
              <EmailDetailPane
                key={fullscreenId}
                emailId={fullscreenId}
                onClose={() => setFullscreenId(null)}
                allEmails={all}
                onOpenEmail={(id) => { openEmail(id); setFullscreenId(id) }}
              />
            </div>
          </div>
        </div>
      )}
      <ComposeModal
        open={composeOpen}
        onClose={() => { setComposeOpen(false); setComposeDraft(null) }}
        tenantSlug={tenant}
        postfachVorwahl={pf.wahl && pf.wahl !== HAUPT ? pf.wahl : ""}
        restoreDraft={composeDraft}
        onSent={() => refetch()}
      />
      <FlagNoteModal
        open={flagModalEmail !== null}
        subject={flagModalEmail?.subject || ""}
        initialNote={flagModalEmail?.flag_note || ""}
        onSave={(note) => {
          if (!flagModalEmail) return
          flagMutation.mutate({
            emailId: flagModalEmail.id,
            flagged: true,
            note: note || null,
          })
          setFlagModalEmail(null)
        }}
        onClose={() => setFlagModalEmail(null)}
      />
      {/* #55: echter IMAP-Spam-Ordner, live vom Server */}
      {spamOffen && tenant && (
        <SpamModal tenant={tenant} postfach={pf.wahl} onClose={() => setSpamOffen(false)} />
      )}
    </div>
  )
}

/** Eine Zeile in der Liste — klickbar (kein Link mehr). */
/**
 * Eine Zeile in der Thread-Ansicht ist eine UNTERHALTUNG, keine einzelne Mail.
 * Wer sie ankreuzt und „Gelesen" klickt, meint die ganze Unterhaltung — vorher
 * ging nur die neueste Mail des Threads durch, die aelteren blieben ungelesen.
 * Genau so entstand der Eindruck „die Mehrfachauswahl klappt nicht
 * zuverlaessig" (gemeldet 20.08.2026).
 *
 * Nur Mails aus der aktuellen Ansicht: was in einem anderen Ordner liegt, wird
 * nicht mitgeloescht.
 */
export function expandThreadSelection(
  ids: number[],
  heads: Array<{ email: { id: number }; threadMails: Array<{ id: number }> }>,
): number[] {
  const raus = new Set<number>()
  for (const id of ids) {
    raus.add(id)
    const head = heads.find((h) => h.email.id === id)
    for (const m of head?.threadMails || []) raus.add(m.id)
  }
  return Array.from(raus)
}

/**
 * Leiste ueber der Liste, sobald Mails markiert sind.
 *
 * Bewusst nur drei Aktionen (Wunsch 20.08.2026): gelesen, ungelesen, loeschen.
 * Verschieben passiert per Drag&Drop auf den Ordner, „Als erledigt" gehoert in
 * die Detailansicht — beides stand hier nur im Weg.
 */
export function BulkToolbar({
  count, pending, loeschBestaetigung, setLoeschBestaetigung, onClear, onAction, onSpam,
}: {
  count: number
  pending: boolean
  loeschBestaetigung: boolean
  setLoeschBestaetigung: (v: boolean) => void
  onClear: () => void
  onAction: (action: "read" | "unread" | "delete") => void
  /** #196: „Als Spam" für die ganze Auswahl (nur wo es Sinn ergibt). */
  onSpam?: () => void
}) {
  return (
    <>
      <button
        onClick={onClear}
        title="Auswahl aufheben"
        style={{
          background: "none", border: "none", padding: 0, cursor: "pointer",
          font: "inherit", fontWeight: 700, color: "inherit",
        }}
      >{count} ausgewählt ✕</button>
      <button
        className="pp-btn pp-btn-sm"
        onClick={() => onAction("read")}
        disabled={pending}
      >Gelesen</button>
      <button
        className="pp-btn pp-btn-sm"
        onClick={() => onAction("unread")}
        disabled={pending}
      >Ungelesen</button>
      {onSpam && (
        <button
          className="pp-btn pp-btn-sm ev2-ab-spam"
          data-testid="bulk-spam-knopf"
          onClick={onSpam}
          disabled={pending}
          title="Alle markierten Mails in den Spam-Ordner schieben; ihre Absender werden künftig automatisch einsortiert"
        >Als Spam</button>
      )}
      {/* Zwei Klicks statt confirm(): Loeschen ist nicht ohne weiteres
          rueckholbar, und ein natives confirm() blockiert die Frontend-Tests. */}
      <button
        className="pp-btn pp-btn-sm"
        style={{
          marginLeft: "auto",
          ...(loeschBestaetigung ? { color: "#dc2626", borderColor: "#dc2626" } : {}),
        }}
        onClick={() => {
          if (!loeschBestaetigung) { setLoeschBestaetigung(true); return }
          setLoeschBestaetigung(false)
          onAction("delete")
        }}
        disabled={pending}
      >{loeschBestaetigung ? "Wirklich löschen?" : "Löschen"}</button>
    </>
  )
}

/** Postfach-Kennung (Mandant mit mehreren Postfaechern). Farbe je Postfach
 *  fest aus dem Namen abgeleitet — dasselbe Postfach sieht immer gleich aus. */
const POSTFACH_FARBEN = ["#0f766e", "#7c3aed", "#b45309", "#be185d", "#1d4ed8", "#15803d", "#9f1239", "#4d7c0f"]
function postfachFarbe(label: string): string {
  let h = 0
  for (const ch of label.toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return POSTFACH_FARBEN[h % POSTFACH_FARBEN.length]
}
function PostfachChip({ label }: { label: string }) {
  const farbe = postfachFarbe(label)
  const kurz = label.includes("@") ? label.split("@")[0] + "@" : label
  return (
    <span
      data-testid="postfach-chip"
      title={`Eingegangen im Postfach ${label}`}
      style={{
        fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 4,
        border: `1px solid ${farbe}`, color: farbe, whiteSpace: "nowrap",
        maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0,
      }}
    >
      {kurz}
    </span>
  )
}

function EmailRow({ email: e, active, selected, threadCount, selectedIds, herkunftZeigen, onSelect, onClick, onDoubleClick, onFlagToggle, onTerminErledigen, onSpam, onZiehen }: {
  email: EmailItem; active: boolean; selected?: boolean; threadCount?: number;
  selectedIds?: number[];
  /** Im Suchergebnis den Ordner mitzeigen, aus dem der Treffer stammt. */
  herkunftZeigen?: boolean;
  onSelect?: (checked: boolean) => void;
  onClick: () => void; onDoubleClick?: () => void;
  onFlagToggle?: () => void;
  /** #35: „Termin war – erledigen" (nur, wenn die Zeile „Termin war am …" sagt). */
  onTerminErledigen?: () => void;
  /** #196: Schnellaktion „Als Spam" in der Zeile (Hover bzw. immer auf Touch). */
  onSpam?: () => void;
  /** Ziehen beginnt/endet — die Ordner klappen dann als Ablage auf. */
  onZiehen?: (aktiv: boolean) => void;
  /** Klassische Ansicht: alter Status-Chip und Vorschau statt „was fehlt". */
}) {
  const isUnread = e.status === "new"
  const avatarBg = avatarColor(e.sender || e.sender_email || "")
  const initials = senderInitials(e.sender || e.sender_email || "")
  return (
    <div
      draggable
      onDragStart={(ev) => {
        // Wenn die Mail Teil der Auswahl ist: alle markierten mitschleppen.
        // Sonst: nur diese eine.
        const ids = selected && selectedIds && selectedIds.length > 0 ? selectedIds : [e.id]
        ev.dataTransfer.effectAllowed = "move"
        ev.dataTransfer.setData("application/x-email-ids", JSON.stringify(ids))
        ev.dataTransfer.setData("text/plain", String(e.id))
        // Herkunft mitgeben, damit der Zielordner "Rueckgaengig" anbieten kann.
        // Die Liste zeigt immer genau einen Ordner, deshalb gilt der Ordner
        // dieser Zeile auch fuer eine Mehrfachauswahl.
        ev.dataTransfer.setData(HERKUNFT_MIME, e.imap_folder || "INBOX")
        onZiehen?.(true)
      }}
      onDragEnd={() => onZiehen?.(false)}
      onClick={(ev) => { if ((ev.target as HTMLElement).tagName === "INPUT") return; onClick() }}
      onDoubleClick={onDoubleClick}
      data-mail-zeile={e.id}
      className={cn("ev2-row", isUnread && "unread", active && "active", selected && "selected")}
      style={{ cursor: "pointer" }}
      title="Doppelklick für Vollbild-Ansicht · Ziehen zum Verschieben in Ordner"
    >
      {onSelect && (
        <input
          type="checkbox"
          checked={selected || false}
          onChange={(ev) => { ev.stopPropagation(); onSelect(ev.target.checked) }}
          onClick={(ev) => ev.stopPropagation()}
          style={{ marginRight: 8, cursor: "pointer" }}
        />
      )}
      <span className={cn("ev2-unread-dot", !isUnread && "read")} />
      <span className="ev2-avatar" data-pii style={{ background: avatarBg }}>{initials}</span>
      <div className="ev2-row-content">
        <div className="ev2-row-top">
          {/* Gesendetes zeigt den Empfaenger — der Absender ist dort immer
              der eigene Betrieb und sagt nichts. */}
          <span className="ev2-sender" data-pii>
            {e.email_direction === "sent"
              ? `An ${prettySender(e.recipient, e.recipient)}`
              : prettySender(e.sender, e.sender_email)}
          </span>
          {/* Mehrere Postfaecher je Mandant: in welchem ist die Mail gelandet?
              Nur gesetzt, wenn der Mandant mehr als ein Postfach hat. */}
          {e.postfach_label && <PostfachChip label={e.postfach_label} />}
          {/* Im Suchergebnis stammen die Treffer aus allen Ordnern. Ohne diese
              Angabe sieht eine gesendete Antwort aus wie eine im Posteingang. */}
          {herkunftZeigen && (
            <span
              className="text-muted-foreground"
              style={{
                fontSize: 11, padding: "1px 6px", borderRadius: 4,
                border: "1px solid var(--border-color)", whiteSpace: "nowrap",
              }}
              title={`Liegt in: ${e.imap_folder || "INBOX"}`}
            >
              {e.imap_folder || "INBOX"}
            </span>
          )}
          {threadCount && threadCount > 1 && (
            <span
              style={{
                fontSize: 12, fontWeight: 600, padding: "1px 6px",
                background: "var(--accent-light, rgba(99,102,241,.15))",
                color: "var(--accent-color, #4f46e5)",
                borderRadius: 10,
              }}
              title={`Thread mit ${threadCount} E-Mails`}
            >
              {threadCount}
            </span>
          )}
          {e.has_attachments && <Paperclip className="w-[12px] h-[12px] text-muted-foreground shrink-0" />}
          {e.is_replied && <span style={{ color: "var(--success-color)", fontSize: 12 }}>↩ beantw.</span>}
          {e.is_forwarded && <span style={{ color: "#2563eb", fontSize: 12 }}>→ weitergeleitet</span>}
          {/* #90 (Marlene, 10.09.): „ein kleiner Status so wie beim KI Telefon".
              ↩/→ sagen, WIE reagiert wurde — dieser Chip, wie weit die Sache ist. */}
          {(e.offene_punkte?.length ?? 0) === 0 && (() => {
            // #90: ohne offene Punkte nur noch „wartet"/„erledigt" — ein „offen"
            // ohne Inhalt sagte nichts (es stand auch an Newslettern).
            const stand = mailStand(e)
            if (stand === "offen") return null
            const farbe = STAND_FARBE[stand]
            return (
              <span
                style={{
                  fontSize: 11, padding: "1px 6px", borderRadius: 10,
                  background: farbe.bg, color: farbe.fg, whiteSpace: "nowrap",
                }}
                title={stand === "wartet"
                  ? "Es wurde geantwortet oder weitergeleitet, abgehakt ist die Mail aber noch nicht"
                  : undefined}
              >
                {STAND_TEXT[stand]}
              </span>
            )
          })()}
        </div>
        <div className="ev2-row-subject" data-pii>{e.subject || "(ohne Betreff)"}</div>
        {/* „Was fehlt noch" (14.10.2026) — dieselbe Regel wie das Review.
            Eigene Zeile, höchstens zwei Hinweise; dafür entfällt die Vorschau. */}
        {((e.offene_punkte?.length ?? 0) > 0 || e.zustaendig || (e.notiz_anzahl ?? 0) > 0) ? (
          <div className="ev2-row-offen">
            {(e.offene_punkte || []).slice(0, 2).map((p) => (
              <span key={p.key} className={p.key === "wiedervorlage" ? "ev2-offen-mini dringend"
                : p.key === "wartet" || p.key === "kunde_wartet" ? "ev2-offen-mini wartet" : HINWEIS_PUNKTE.includes(p.key) ? "ev2-offen-mini danach" : "ev2-offen-mini"}>
                {p.label}
              </span>
            ))}
            {/* #35 (Paul 21.10.2026): Termin vorbei → ein Klick erledigt die
                Mail. Nie automatisch — die Zeile fragt nur. */}
            {onTerminErledigen && e.status !== "done" && (e.offene_punkte || []).some((p) => p.key === "termin_war") && (
              <button type="button" className="ev2-offen-mini danach" data-testid="termin-war-erledigen"
                onClick={(ev) => { ev.stopPropagation(); onTerminErledigen() }}>
                Termin war – erledigen
              </button>
            )}
            <span style={{ flex: 1 }} />
            {(e.notiz_anzahl ?? 0) > 0 && (
              <span className="ev2-row-notizen" title={`${e.notiz_anzahl} interne Notiz(en)`}>
                <StickyNote className="w-3 h-3" aria-hidden />{e.notiz_anzahl}
              </span>
            )}
            {e.zustaendig && <PersonPunkt name={e.zustaendig} groesse={20} />}
          </div>
        ) : e.handlungsbedarf ? (
          <div className="ev2-row-preview" data-pii>{e.handlungsbedarf}</div>
        ) : null}
      </div>
      <div className="ev2-row-meta">
        <span className="ev2-row-date">{fmtDate(e.received_at)}</span>
        {e.category && (
          <VorgehenChip
            tenantSlug={e.tenant_slug}
            kategorie={e.category}
            mitIcon
            className="ev2-row-cat"
            style={{ background: "var(--accent-light)", color: "var(--accent-color)" }}
          />
        )}
      </div>
      {onSpam && (
        <span
          role="button"
          tabIndex={0}
          data-testid="zeile-spam-knopf"
          className="ev2-row-spam"
          title="Als Spam wegsortieren – künftige Mails dieses Absenders landen automatisch im Spam-Ordner"
          aria-label="Als Spam wegsortieren"
          onClick={(ev) => { ev.stopPropagation(); onSpam() }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); ev.stopPropagation(); onSpam() }
          }}
        >
          <ShieldAlert size={14} />
        </span>
      )}
      <span
        role="button"
        tabIndex={0}
        title={e.flagged ? (e.flag_note || "Markiert (Klick = entfernen)") : "Zur Bearbeitung markieren"}
        onClick={(ev) => { ev.stopPropagation(); onFlagToggle?.() }}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); ev.stopPropagation(); onFlagToggle?.() }
        }}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 24, height: 24, borderRadius: 4, cursor: "pointer", flexShrink: 0,
          marginLeft: 8,
          background: e.flagged ? "rgba(220,38,38,0.1)" : "transparent",
        }}
      >
        <Flag
          size={14}
          fill={e.flagged ? "#dc2626" : "transparent"}
          color={e.flagged ? "#dc2626" : "#bbb8b1"}
          strokeWidth={e.flagged ? 0 : 1.8}
        />
      </span>
    </div>
  )
}

/** Detail-Pane rechts: volle E-Mail mit AI-Panel, HERO-Section, Body, Reply. */
/** Kurzer Fingerabdruck eines Textes — als React-Key.
 *
 * Die Laenge allein reicht nicht: eine neu geschriebene Antwort mit anderem
 * Termin kann exakt gleich lang sein. Kein Krypto-Anspruch, nur "hat sich
 * etwas geaendert".
 */
function textSchluessel(text: string): string {
  let h = 0
  for (let i = 0; i < text.length; i++) {
    h = (h * 31 + text.charCodeAt(i)) | 0
  }
  return `${text.length}-${(h >>> 0).toString(36)}`
}

export function EmailDetailPane({ emailId, onClose, allEmails, onOpenEmail, onTenantErkannt, karte, ganzeMailZeigen, onErledigt, alsGelesen }: {
  emailId: number; onClose: () => void;
  allEmails?: EmailItem[]; onOpenEmail?: (id: number) => void
  /** Meldet den Mandanten der geladenen Mail — die Liste zieht dann nach. */
  onTenantErkannt?: (tenantSlug: string) => void
  /** Neues Postfach (28.10.2026): Kopf mit HERO-Zeile und Reitern, die volle
   *  Plan-Karte gleich unter der Mail, alle Aktionen in der Fußleiste. Es
   *  fällt NICHTS weg — dieselben Bausteine, anders angeordnet. */
  karte?: boolean
  /** Karte: die Mail ganz aufgeklappt zeigen (Topf „Von Hand“). Sonst steht
   *  oben ein Auszug, und „Ganze Mail anzeigen“ klappt sie auf. */
  ganzeMailZeigen?: boolean
  /** Karte: nach „Selbst erledigt“ zur nächsten Mail. */
  onErledigt?: () => void
  /** Karte: beim Öffnen als gelesen markieren (im Portal und im Postfach).
   *  Nur, wenn jemand die Mail gewählt hat — nicht, wenn das Postfach von
   *  selbst die erste offene zeigt. */
  alsGelesen?: boolean
}) {
  const qc = useQueryClient()
  // Quick-Forward-Prefill (von ActionBar-Rule-Button an ForwardForm)
  const [fwdPrefill, setFwdPrefill] = useState<{ to: string; signal: number }>({ to: "", signal: 0 })
  // 🔴 Keine eigene `queryFn` mehr — sie gehoert dem QueryClient
  // (`lib/emailQuery.ts`). Solange jeder der drei Leser dieses Schluessels die
  // Abfrage selbst mitdefinierte, konnte jeder sie den anderen wegnehmen:
  // genau daraus entstand „Missing queryFn" dreimal (07.08., 02.09., 04.09.).
  const { data, isLoading, error } = useQuery<EmailAntwort>({
    queryKey: ["email", String(emailId)],
  })
  // Wie im Mailprogramm: wer die Mail öffnet, hat sie gelesen — einmal je
  // Öffnen. Wer sie danach auf „ungelesen“ stellt, dem dreht das nichts zurück.
  const gelesenSetzen = useGelesen()
  const autoGelesen = useRef(false)
  useEffect(() => {
    const m = data?.email
    if (!alsGelesen || autoGelesen.current || !m || m.id !== emailId) return
    autoGelesen.current = true
    if (m.gelesen === false && m.email_direction !== "sent") gelesenSetzen([m.id], true, true)
  }, [alsGelesen, data?.email, emailId]) // eslint-disable-line react-hooks/exhaustive-deps
  // Pending Doppel-Termin-Cleanup — wenn Vor-Mail im Thread zwei HERO-Events angelegt hat
  const pendingCleanupQ = useQuery({
    queryKey: ["pending-cleanup", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/pending-cleanup`, { credentials: "include" })
      if (!r.ok) return null
      return r.json()
    },
    staleTime: 1000 * 30,
  })
  // Anhang-IDs fuer ForwardForm (share-query mit AttachmentsSection).
  const attachmentsQ = useQuery({
    queryKey: ["attachments", emailId],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/emails/{email_id}/attachments", {
        params: { path: { email_id: emailId } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 60,
  })
  const attachmentIds = useMemo(
    () => (attachmentsQ.data?.attachments || []).map((a) => a.id),
    [attachmentsQ.data],
  )
  // Der Antwort-Entwurf ist zugeklappt, bis ihn jemand oeffnet: der
  // "Antworten"-Knopf oben oder "Ansehen" an der Antwort-Zeile im Plan.
  const [replyOpen, setReplyOpen] = useState(false)
  const replyRef = useRef<HTMLDivElement | null>(null)
  const entwurfOeffnen = () => {
    // Karte: Antwortfeld steht im Reiter „Erledigen“.
    if (karte) setSeite("erledigen")
    setReplyOpen(true)
    setTimeout(() => replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60)
  }
  // Der Plan liegt im selben Cache, den InboxActionPlan fuellt. Wir lesen ihn
  // nur mit — ohne eigenen Request.
  //
  // 🔴 Weder Dummy-queryFn noch `skipToken`: beide definieren die Abfrage mit
  // und nehmen dem Eigentuemer (`InboxActionPlan`) etwas weg — einmal die
  // Daten, einmal die Funktion selbst. Dasselbe Muster liess am 02.09.2026
  // eine Mail mit „Missing queryFn" gar nicht mehr aufgehen. Siehe
  // `lib/cacheMitleser.ts`.
  const planCache = useCacheMitleser<{
    reasoning?: string
    planweg?: string | null
    monteur_task?: {
      title?: string | null; body?: string | null
      due_date?: string | null; target_user_id?: number | null; target_user_name?: string | null
    } | null
    reply?: { subject?: string; body?: string }
    project?: { title?: string; measure_id?: number | null; measure_name?: string; type_id?: number | null; project_type_id?: number | null }
  }>(["draft-plan", emailId])
  const planReply = planCache?.reply
  const planProject = planCache?.project
  const [entwurfBeruehrt, setEntwurfBeruehrt] = useState(false)
  // Schluessel des Antwort-Formulars. Solange niemand getippt hat, folgt er
  // dem Plan-Text — ein neu geschriebener Text soll das Formular neu
  // befuellen. Ab der ersten Aenderung bleibt er stehen.
  //
  // 🔴 Er darf sich dabei NICHT aendern. Frueher sprang er auf "beruehrt",
  // und genau dieser Sprung baute das Formular beim ersten Zeichen komplett
  // neu auf — mitten im Schreiben. Den zuletzt gueltigen Wert einfach
  // stehenzulassen erfuellt denselben Zweck ohne Neuaufbau.
  const antwortKey = useRef("roh")
  if (!entwurfBeruehrt) {
    antwortKey.current = planReply?.body ? `plan:${textSchluessel(planReply.body)}` : "roh"
  }
  const [heroOpen, setHeroOpen] = useState(false)
  const [bookOpen, setBookOpen] = useState(false)
  // Rechte Spalte als Reiter (14.10.2026): Notizen · Kunde · Wissen · Verlauf.
  // Ohne Wahl: Notizen, wenn es welche gibt — sonst der Kunde.
  const [seiteGewaehlt, setSeite] = useState<"erledigen" | "notizen" | "kunde" | "wissen" | "verlauf" | null>(null)
  const [ganzeMail, setGanzeMail] = useState(!!ganzeMailZeigen)
  const [uebergabeOffen, setUebergabeOffen] = useState(false)
  const planRef = useRef<HTMLDivElement | null>(null)
  // Tastenkürzel an der geöffneten Mail: R antworten, E erledigt, N Notiz,
  // V verschieben. Über Refs, damit der Handler nicht bei jedem Rendern neu hängt.
  const tastenDetail = useRef<{ r: () => void; e: () => void; n: () => void }>({ r: () => {}, e: () => {}, n: () => {} })
  useEffect(() => {
    const aufTaste = (ev: KeyboardEvent) => {
      if (!tasteZaehlt(ev)) return
      const k = ev.key.toLowerCase()
      if (k === "r") { ev.preventDefault(); tastenDetail.current.r() }
      else if (k === "e") { ev.preventDefault(); tastenDetail.current.e() }
      else if (k === "n") { ev.preventDefault(); tastenDetail.current.n() }
      else if (k === "v") {
        const knopf = document.querySelector<HTMLButtonElement>('[data-taste="verschieben"]')
        if (knopf) { ev.preventDefault(); knopf.click() }
      }
    }
    window.addEventListener("keydown", aufTaste)
    return () => window.removeEventListener("keydown", aufTaste)
  }, [])
  // Termin-Einladung aus dem Mailtext (#29) — der Block stammt aus dem
  // IMAP-Sync und steht im body_text, es braucht also keine neue Spalte
  // und es gilt auch fuer Mails, die schon da sind.
  // Im Fliesstext genannte Personen (#15) — nicht der Absender, sondern die
  // Dritten: „Koordination mit Hausmeister Herr Testkamp (0176/…)".
  const [personPrefill, setPersonPrefill] = useState<
    { first: string; last: string; phone: string } | null
  >(null)
  const personen = useMemo(() => {
    const e = data?.email
    if (!e) return []
    return ohneAbsender(personenImText(e.body_text), e.sender || e.sender_email)
  }, [data?.email])

  // Mandant der geladenen Mail nach oben melden (25.08.2026) — sonst bleibt
  // die Liste auf dem Betrieb stehen, der vorher gewaehlt war.
  //
  // 🔴 `onTenantErkannt` gehoert NICHT in die Abhaengigkeiten (#45). Der
  // Rueckruf haengt oben am Mandanten, ist nach jedem Wechsel eine neue
  // Referenz — und dann meldete dieser Effekt den Mandanten der noch offenen
  // Mail nach, was den Wechsel des Nutzers sofort zurueckdrehte. Er soll auf
  // die geladene MAIL reagieren, nicht auf die Referenz.
  const gemeldeterTenant = data?.email?.tenant_slug
  const melden = useRef(onTenantErkannt)
  melden.current = onTenantErkannt
  useEffect(() => {
    if (gemeldeterTenant) melden.current?.(gemeldeterTenant)
  }, [emailId, gemeldeterTenant])

  const einladung = useMemo(
    () => terminEinladungLesen(data?.email?.body_text),
    [data?.email?.body_text],
  )
  // Termin aus der gerade verschickten Antwort (#19).
  const [blockerSlot, setBlockerSlot] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createWithProj, setCreateWithProj] = useState(true)
  const [createProjectOnly, setCreateProjectOnly] = useState(false)
  const [ansprechpartnerOpen, setAnsprechpartnerOpen] = useState(false)
  const contactRestore = useContactDraftRestore(emailId)

  // fortytools-Verknüpfungs-Info (Kunde+Objekte / Bestandsabgleich per E-Mail).
  // HERO-Tenants -> {is_forty:false} -> normale HERO-Karte, unverändert.
  const fortyInfoQ = useQuery({
    queryKey: ["forty-link-info", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/forty-link-info`, { credentials: "include" })
      if (!r.ok) return { is_forty: false }
      return r.json()
    },
    enabled: !!emailId,
  })
  const isForty = !!fortyInfoQ.data?.is_forty

  // Verknüpfungs-Vorschläge (HERO-Tenants): frühere manuelle Zuordnung
  // desselben Absenders + HERO-Kontakte mit dieser Adresse. Wird NIE
  // automatisch angewendet — mehrere Kontakte können sich eine Adresse teilen.
  const linkSuggQ = useQuery<LinkSuggestions>({
    queryKey: ["link-suggestions", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/link-suggestions`, { credentials: "include" })
      if (!r.ok) return { historie: [], hero_treffer: [], mehrdeutig: false, hinweis: "" }
      return r.json()
    },
    // Auch laden, wenn der Kontakt schon hängt und nur der Vorgang fehlt: der
    // Endpunkt liefert dann zwar keine Kontakt-Vorschläge mehr, aber den
    // Vorgang laut Plan — genau die Lücke, die Stufe 3 schließt.
    enabled: !!emailId && !isForty
      && (!data?.email?.hero_contact_id || !data?.email?.hero_project_id),
  })

  // Lazy-Refetch von body_html fuer alte Mails (vor der body_html-Migration gespeichert).
  // Erkannt an body_html==null + einer Heuristik: entweder body_text enthaelt HTML-Tags
  // (dann war die Original-Mail HTML) oder sie ist sehr lang (>500 Zeichen, lohnt sich).
  // Zusaetzlich: refetch wenn iCal-Anhaenge vorhanden sind aber der Termin-Block
  // (Marker "<!-- ical-invitation-block -->") noch nicht eingebaut ist — passiert
  // bei alten Mails die vor dem iCal-Parsing-Patch importiert wurden.
  useEffect(() => {
    const em = data?.email
    if (!em) return
    const atts = (attachmentsQ.data?.attachments || []) as Array<{ filename?: string | null; content_type?: string | null }>
    const hasIcalAttachment = atts.some(a =>
      (a.content_type || "").toLowerCase().startsWith("text/calendar") ||
      (a.filename || "").toLowerCase().endsWith(".ics")
    )
    const html = em.body_html || ""
    const hasIcalBlock = html.includes("ical-invitation-block")
    const needsIcalRefetch = hasIcalAttachment && !hasIcalBlock

    if (em.body_html && !needsIcalRefetch) return
    if (!em.body_html) {
      const txt = em.body_text || ""
      const looksLikeHtml = /<\/[a-z]+>|<(html|body|div|p|br|table|td|tr|span|img|style|meta|!doctype)\b/i.test(txt)
      const isLong = txt.length > 500
      if (!looksLikeHtml && !isLong) return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/v1/emails/${emailId}/refetch-body`, {
          method: "POST",
          credentials: "include",
        })
        if (!res.ok || cancelled) return
        qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      } catch { /* ignorieren, ReplyForm nutzt dann body_text-Fallback */ }
    })()
    return () => { cancelled = true }
  }, [data?.email, emailId, qc, attachmentsQ.data])

  // Thread-Mails vom Server holen — die geladene Liste umfasst nur ~200 Mails,
  // aeltere Thread-Mitglieder (z.B. das urspruengliche Angebot) fehlten sonst.
  const threadQ = useQuery({
    queryKey: ["email-thread", emailId, data?.email?.thread_id || ""],
    queryFn: async (): Promise<EmailItem[]> => {
      const r = await fetch(`/api/v1/emails/${emailId}/thread`, { credentials: "include" })
      if (!r.ok) return []
      const j = await r.json()
      return (j?.items || []) as EmailItem[]
    },
    enabled: !!data?.email?.thread_id,
    staleTime: 15_000,
  })
  const threadMails = useMemo(() => {
    if (!data?.email) return []
    const tid = data.email.thread_id
    if (!tid) return []
    // Server-Ergebnis bevorzugen; Fallback: Filter ueber die geladene Liste
    const source = (threadQ.data && threadQ.data.length > 0)
      ? threadQ.data
      : (allEmails || []).filter((m) => m.thread_id === tid)
    return [...source].sort((a, b) => {
      const da = new Date(a.received_at || "").getTime()
      const db = new Date(b.received_at || "").getTime()
      return db - da
    })
  }, [data?.email, allEmails, threadQ.data])

  if (isLoading) return <Loading label="Lädt E-Mail..." />
  // 🔴 Nie den rohen Bibliothekstext zeigen: „Missing queryFn: ['email','7299']"
  // stand so in der Inbox und wurde als „mails verschwinden" gemeldet.
  if (error) return (
    <div className="p-5 text-destructive">
      Diese E-Mail konnte nicht geladen werden. Bitte die Seite neu laden — bleibt es dabei, bitte melden.
    </div>
  )
  if (!data?.email) return null

  const e = data.email
  const seite = seiteGewaehlt ?? (karte ? "erledigen" : (e.notiz_anzahl || 0) > 0 ? "notizen" : "kunde")
  const zeige = (k: "notizen" | "kunde" | "wissen" | "verlauf") => seite === k
  const zeigeHaupt = !karte || seite === "erledigen"
  tastenDetail.current = {
    r: () => entwurfOeffnen(),
    e: async () => {
      if (e.status === "done") return
      const { error } = await api.PATCH("/api/v1/emails/{email_id}", { params: { path: { email_id: emailId } }, body: { status: "done" } })
      if (error) { toast("Konnte nicht als erledigt markieren", "error"); return }
      toast("Als erledigt markiert", "success")
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["emails"] })
      qc.invalidateQueries({ queryKey: ["mail-ansichten"] })
    },
    n: () => {
      if (e.email_direction === "sent") return
      setSeite("notizen")
      setTimeout(() => document.getElementById(`ev2-notiz-${emailId}`)?.focus(), 80)
    },
  }

  return (
    <>
      {/* Kopf: Betreff, eine Metazeile, daneben die Aktionen. Der Avatar ist
          weg — er sagte nichts, was nicht daneben stand. Empfaenger und Ordner
          stehen im Titel-Attribut statt in der Zeile. */}
      {karte && (
        <KarteKopf e={e} emailId={emailId} seite={seite === "wissen" ? "kunde" : seite}
          onSeite={(k) => setSeite(k)} notizAnzahl={e.notiz_anzahl || 0} />
      )}
      {!karte && <div className="ev2-detail-header">
        <div className="ev2-detail-main">
          <div className="ev2-detail-subject" data-pii>{e.subject || "(ohne Betreff)"}</div>
          {/* Bei gesendeten Mails ist der Absender immer der eigene Betrieb —
              die Zeile stand also zweimal fuer dasselbe da, und an WEN die
              Mail ging, war nur im Tooltip zu ahnen. Bei "sent" zeigen wir
              deshalb den Empfaenger (10.08.2026: eine Terminbestaetigung an
              Kunde A las sich im Portal als "info@sanitec-shk.de ·
              info@sanitec-shk.de"). */}
          <div className="ev2-detail-meta" data-pii title={[e.recipient && `An ${e.recipient}`, e.imap_folder].filter(Boolean).join(" · ")}>
            {e.postfach_label && <PostfachChip label={e.postfach_label} />}
            {e.email_direction === "sent" ? (
              <>
                <strong>An {cleanSender(e.recipient) || "—"}</strong>
                {extractEmail(e.recipient) && <span>{extractEmail(e.recipient)}</span>}
              </>
            ) : (
              <>
                <strong>{cleanSender(e.sender)}</strong>
                {extractEmail(e.sender) && <span>{extractEmail(e.sender)}</span>}
              </>
            )}
            <span>{fmtDateLong(e.received_at)}</span>
          </div>
          {/* Zweite Zeile: der vollstaendige Verteiler. Wer eine Mail bekam und
              wer nur in Kopie, entscheidet oft darueber, wer antworten muss —
              das stand bisher nirgends (gemeldet 10.08.2026). Cc gibt es erst
              fuer Mails, die seit dem 10.08.2026 abgeholt wurden; davor wurde
              der Header beim Import verworfen. */}
          {(e.recipient || e.cc) && (
            <div className="ev2-detail-meta" data-pii style={{ opacity: 0.85, flexWrap: "wrap" }}>
              {e.email_direction === "sent"
                ? extractEmail(e.sender) && <span>Von {cleanSender(e.sender)}</span>
                : e.recipient && <span>An {e.recipient}</span>}
              {e.cc && <span><strong>Cc</strong> {e.cc}</span>}
            </div>
          )}
          {/* #155: Mail-ID fuer Screenshot und „Problem melden" — ausserhalb
              von data-pii, damit die Schwaerzung sie stehen laesst. */}
          <div style={{ marginTop: 2 }}><MailIdMarke id={emailId} /></div>
        </div>
        <div className="ev2-kopf-rechts">
          {e.email_direction !== "sent" && (
            <button type="button" className="ev2-zustaendig" onClick={() => setUebergabeOffen(true)}
              title="Zuständig – klicken zum Übergeben">
              <PersonPunkt name={e.zustaendig} groesse={26} titel="" />
              {e.zustaendig ? anzeigeName(e.zustaendig) : "Zuweisen"}
            </button>
          )}
          <ActionBar email={e} emailId={emailId} onClose={onClose} onReply={entwurfOeffnen}
            aufgabenText={aufgabenVorbelegung({
              monteurTask: planCache?.monteur_task,
              handlungsbedarf: e.handlungsbedarf,
              mailText: e.body_text,
            })}
            aufgabePlan={aufgabeAusPlan(planCache)}
            onQuickForward={(to) => setFwdPrefill({ to, signal: Date.now() })} />
        </div>
      </div>}
      {uebergabeOffen && (
        <UebergabeDialog emailId={emailId} offen onClose={() => setUebergabeOffen(false)} aktuell={e.zustaendig} />
      )}

      {/* „Noch offen": alles, was jemand an dieser Mail noch tun muss —
          dieselbe Regel wie das Review (services/mail_offen.py). */}
      {e.email_direction !== "sent" && !karte && (
        <NochOffenLeiste
          emailId={emailId}
          punkte={e.offene_punkte || []}
          status={e.status}
          zustaendig={e.zustaendig}
          wiedervorlageAm={e.wiedervorlage_am}
          onPunkt={(key) => {
            if (key === "todo") setSeite("notizen")
            else if (key === "danach" || key === "ausgang" || key === "termin_war") setSeite("verlauf")
            else if (key === "wartet") setSeite("verlauf")
            else if (key === "antwort_senden") entwurfOeffnen()
            else if (key === "wiedervorlage") setSeite("notizen")
            else planRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
          }}
        />
      )}

      {/* Ab hier zwei Spalten: links die Mail und was mit ihr geschieht,
          rechts alles, was sie einordnet (Kontakt, Kategorie, Verlauf).
          Vorher stand das untereinander und schob die Arbeit nach unten. */}
      <div className="ev2-detail-scroll">
      <div className={karte ? "ev2-detail-grid pk-grid" : "ev2-detail-grid"}>
      {zeigeHaupt && <div className="ev2-detail-haupt">

      {/* Karte: Ist die Mail erledigt, steht oben, wie der Plan endete und
          warum — ausgeführt, anders erledigt, liegen geblieben (Paul 28.10.). */}
      {karte && e.status === "done" && e.email_direction !== "sent" && (
        <div className="pk-ausgang-oben"><PlanAusgangBlock emailId={emailId} /></div>
      )}

      {/* #197: Hat die Mail inzwischen einen Termin, steht das als Erstes da. */}
      <TerminBestaetigtBanner termin={(e as { termin_bestaetigt?: TerminBestaetigt | null }).termin_bestaetigt} />

      {/* Thread-Timeline (nur wenn Thread mit mehreren Mails) */}
      {threadMails.length > 1 && onOpenEmail && (
        <ThreadTimeline
          mails={threadMails}
          activeId={emailId}
          onSelect={onOpenEmail}
        />
      )}

      {/* 🔴 #30 (Marlene): „warum kann hier die gesendete mail nicht
          aufgeklappt werden". Weil sie nicht in der Datenbank steht — und
          weil der Thread-Streifen erst ab zwei Mails erscheint, fehlte er
          ganz, ohne ein Wort dazu. Am Postfach geprüft: die Vorgänger liegen
          in keinem der 482 Ordner, das Portal kann sie nicht nachholen. Es
          sagt jetzt wenigstens, dass es sie nicht hat. */}
      {threadMails.length <= 1
        && (e as { vorgaenger_fehlt?: boolean }).vorgaenger_fehlt && (
        <div className="ev2-hinweiszeile">
          Diese Mail antwortet auf eine frühere Nachricht, die nicht im Postfach
          liegt — sie lässt sich deshalb nicht aufklappen.
        </div>
      )}

      {/* #163 (Marlene): „beantwortet" ohne sichtbare Antwort. Die Antwort
          ging über das Portal raus, steht aber in keinem Verlauf dieser Mail —
          dann steht sie hier, statt zu fehlen. */}
      {(e as { antwort_ausserhalb_verlauf?: AntwortAusserhalb | null }).antwort_ausserhalb_verlauf && (
        <AntwortAusserhalbVerlauf
          antwort={(e as { antwort_ausserhalb_verlauf?: AntwortAusserhalb | null }).antwort_ausserhalb_verlauf!}
        />
      )}

      {/* Doppel-Termin-Cleanup wenn Kunde geantwortet hat — gleiches Schema wie Review */}
      {pendingCleanupQ.data && (
        <div style={{ padding: "0 20px 12px" }}>
          <PendingCleanupCard
            cleanup={pendingCleanupQ.data}
            onDone={() => {
              qc.invalidateQueries({ queryKey: ["pending-cleanup", emailId] })
              qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
            }}
          />
        </div>
      )}

      {/* Portal-Eintraege (Nachhak, Rueckruf, Telefon) sind keine Mails. Ohne
          diese Zeile liest sich der Erklaertext unten wie ein Kundenschreiben
          (Paul, 07.09.2026). */}
      {(e as { is_proactive?: boolean }).is_proactive && (
        <div className="ev2-hinweiszeile" style={{ margin: "12px 20px 0" }}>
          <strong>Handlungsempfehlung des Portals</strong> — keine eingegangene E-Mail.
          Der Text unten ist die Begruendung des Portals, nicht ein Schreiben des Kunden.
        </div>
      )}
      {/* Body — HTML bevorzugt, Fallback Plain. 68 Zeichen Zeilenlaenge:
          laenger wird das Auge beim Ruecksprung unsicher. */}
      {/* Karte: die Mail des Absenders steht in einem eigenen, leicht
          getönten Block — darunter beginnt sichtbar der Teil des Agenten
          (Paul 28.10.). */}
      <div className={karte ? "pk-mail" : undefined}>
      {karte && (
        <div className="pk-mail-kopf">
          <span className="pk-mail-marke">{e.email_direction === "sent"
            ? <>Gesendet an <span data-pii>{cleanSender(e.recipient) || "—"}</span></>
            : <>Mail von <span data-pii>{cleanSender(e.sender) || e.sender_email || "—"}</span></>}</span>
        </div>
      )}
      <div className={karte && !ganzeMail ? "ev2-detail-body pk-auszug" : "ev2-detail-body"}>
        <div style={{ maxWidth: "68ch" }}>
          {/* Platzhalter raus (25.08.2026): in drei gespeicherten Mails steht
              woertlich `{{BILD:logo}}` im Text. Die Mail ist raus und nicht
              mehr zu reparieren — aber niemand muss sie so lesen. */}
          {e.inhalt_entfernt_am ? (
            // Datenschutz-Löschjob: Inhalt nach 90 Tagen geleert — das
            // Original liegt im Postfach des Kunden.
            <InhaltEntferntHinweis
              tenant={e.tenant_slug}
              messageId={e.message_id}
              folder={e.imap_folder}
              postfach={e.postfach}
              postfachVerfuegbar={e.postfach_verfuegbar}
            />
          ) : (
            <EmailBody
              html={platzhalterEntfernen(e.body_html || e.body_text || "")}
              emailId={e.id}
            />
          )}
        </div>
      </div>
      {karte && !e.inhalt_entfernt_am && (
        <button type="button" className="pk-link pk-auszug-knopf" aria-expanded={ganzeMail}
          onClick={() => setGanzeMail(!ganzeMail)}>
          {ganzeMail ? "Weniger anzeigen" : "Ganze Mail anzeigen"}
        </button>
      )}
      </div>{/* /pk-mail */}

      {/* Attachments direkt nach Body — AttachmentsSection rendert sich selbst weg
          wenn keine Anhaenge vorhanden. has_attachments-Flag ist nicht immer korrekt
          gesetzt (Content-Disposition:inline ohne CID), deshalb immer pruefen. */}
      <div style={{ padding: "0 20px 12px" }}>
        <AttachmentsSection emailId={emailId} tenantSlug={e.tenant_slug}
                            hatteAnhaenge={!!e.has_attachments} />
      </div>

      {/* Schritt C: derselbe Befund, jetzt ausfuehrbar. Laedt nur einen bereits
          vorhandenen Plan (`only_cached`) — Oeffnen einer Mail kostet nichts.
          Die frueher eigenstaendige KI-Analyse-Karte ist darin aufgegangen:
          Kategorie und Handlungsbedarf stehen ohnehin schon in Kopf und
          Listenzeile, der Historie-Badge sitzt jetzt im Kartenkopf. */}
      {e.email_direction !== "sent" && (
        <div ref={planRef} style={{ scrollMarginTop: 12 }}>
          <InboxActionPlan
            kompakt={!karte}
            karte={karte}
            emailId={emailId}
            kopfExtra={<HistoryContextBadge emailId={emailId} />}
            onAntwortAnsehen={entwurfOeffnen}
            threadMails={threadMails}
            onMailOeffnen={onOpenEmail}
            terminUeberholt={!!(e as { termin_bestaetigt?: TerminBestaetigt | null }).termin_bestaetigt?.ueberholt}
          />
        </div>
      )}

      {/* Reply-Form IMMER SICHTBAR (nicht erst bei Klick) — wie altes Portal.
          Kontaktformular-Resolver: effective_reply_to hat Vorrang. */}
      {(e as { effective_reply_to?: string | null }).effective_reply_to && (
        <div className="ev2-hinweiszeile">
          Antwort geht an <strong>{(e as { effective_reply_to?: string | null }).effective_reply_to}</strong>
          {" "}— aus dem Mailtext gelesen (Kontaktformular)
        </div>
      )}
      {/* Kein seitlicher Abstand: die Plan-Karte (.ev2-pk) hat auch keinen,
          und ein Antwort-Fenster, das 20px schmaler ist als die Karte
          darueber, sieht aus wie ein Fehler. Oben/unten bleibt es. */}
      <div ref={replyRef} style={{ padding: "12px 0 20px", borderTop: "1px solid var(--border-color)" }}>
        <ReplyForm
          // Solange niemand getippt hat, darf der Plan-Text nachruecken: er
          // kommt eine Sekunde spaeter als die Mail. Ab der ersten Aenderung
          // steht der Schluessel fest, sonst waere die Eingabe weg.
          //
          // 🔴 Der Schluessel muss am INHALT haengen, nicht nur daran, DASS es
          // einen Plan-Text gibt. Sonst bleibt er bei "plan" stehen, wenn
          // `refresh-body` den Text nach einer Termin-Aenderung neu schreibt —
          // das Formular remountet nicht und zeigt weiter den alten Termin.
          // Genau so gesehen am 07.08.2026: Termin von Hand geaendert, im
          // Antworttext stand weiter der alte (#61082).
          key={antwortKey.current}
          emailId={emailId}
          offen={replyOpen}
          onOffenChange={setReplyOpen}
          // "Neu generieren" plant neu — derselbe Aufruf und dasselbe Modell
          // (Planer) wie im Review, mit und ohne eigene Anweisung. Vorher lief
          // die Mail ueber `/refresh-body` bzw. `/generate-draft` (Haiku) und
          // schrieb spuerbar schlechtere Texte als das Review (14.10.2026).
          planNeuSchreiben={planCache ? async (hinweis?: string) => {
            const r = await fetch(`/api/v1/drafts/${emailId}/replan`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ hint: hinweis || "", current_plan: planCache, stark: false }),
            })
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            const neu = await r.json() as ActionPlan
            // Wie im Review: Haken und gewaehlte Monteure des Nutzers bleiben.
            qc.setQueryData(["draft-plan", emailId], (alt: ActionPlan | undefined) => {
              const slot = <T extends { partner_id?: number | null }>(n: T, a: T | undefined): T =>
                (a?.partner_id != null && n?.partner_id == null ? { ...n, partner_id: a.partner_id } : n)
              return {
                ...neu,
                slot: slot(neu.slot, alt?.slot),
                slot_secondary: slot(neu.slot_secondary, alt?.slot_secondary),
                slot_tertiary: slot(neu.slot_tertiary, alt?.slot_tertiary),
                step_enabled: alt?.step_enabled ?? neu.step_enabled,
              }
            })
            return neu.reply?.body ? { body: neu.reply.body, subject: neu.reply.subject } : null
          } : undefined}
          // Was im Formular steht, ist das, was der Plan verschickt.
          onEntwurfChange={(v) => {
            setEntwurfBeruehrt(true)
            qc.setQueryData(["draft-plan", emailId], (alt: any) => {
              if (!alt) return alt
              // Unveraendert? Dann dasselbe Objekt zurueckgeben. Ein neues
              // Objekt weckt jeden Leser dieser Query — hier die Seite selbst —
              // und rendert sie neu, ohne dass sich etwas geaendert hat.
              const r = alt.reply || {}
              if (r.to === v.to && r.subject === v.subject && r.body === v.body) return alt
              return { ...alt, reply: { ...r, to: v.to, subject: v.subject, body: v.body } }
            })
          }}
          defaultTo={
            (e as { effective_reply_to?: string | null }).effective_reply_to
            || extractReplyAddress(e.reply_to)
            || e.sender_email
            || e.sender
          }
          // Der Plan-Text gilt: er nennt die Termine, die auch gebucht werden.
          // `draft_response` stammt aus der frueheren KI-Analyse und schlug
          // teils ganz andere Tage vor als der Plan spaeter gefunden hat —
          // der Kunde las Mittwoch/Donnerstag/Freitag, gebucht wurde Mo/Di.
          defaultSubject={planReply?.subject || e.subject || ""}
          aiDraft={planReply?.body || e.draft_response}
          tenantSlug={e.tenant_slug}
          // Portal-Entwuerfe haben kein Kundenschreiben zum Zitieren — ihr
          // body_text ist unsere interne Erklaerung. Die darf NIE mitgesendet
          // werden.
          originalHtml={(e as { is_proactive?: boolean }).is_proactive
            ? "" : (e.body_html || stripAttachmentDumps(e.body_text || ""))}
          originalIsPlain={!e.body_html && !/<\/[a-z]+>|<(html|body|div|p|br|table|td|tr|span|img|style|meta|!doctype)\b/i.test(e.body_text || "")}
          originalSender={e.sender || ""}
          originalDate={e.received_at || ""}
          manualDraft={{
            to: e.manual_draft_to,
            cc: e.manual_draft_cc,
            subject: e.manual_draft_subject,
            body: e.manual_draft_body,
            saved_at: e.manual_draft_saved_at,
          }}
          onGesendet={async (text) => {
            // Nur fragen, wenn sich ueberhaupt buchen laesst.
            if (!e.hero_contact_id && !e.hero_project_id) return
            if (e.hero_booking_id) return
            const iso = await terminBlockerFragen(emailId, text)
            if (!iso) return
            setBlockerSlot(iso)
            setBookOpen(true)
          }}
        />
        <div id={`forward-form-${emailId}`}>
          <ForwardForm
            emailId={emailId}
            defaultSubject={e.subject || ""}
            defaultBody=""
            tenantSlug={e.tenant_slug}
            originalAttachmentIds={attachmentIds}
            originalHadAttachments={!!e.has_attachments}
            attachmentsLoading={attachmentsQ.isLoading}
            // Portal-Entwuerfe haben kein Kundenschreiben zum Zitieren — ihr
          // body_text ist unsere interne Erklaerung. Die darf NIE mitgesendet
          // werden.
          originalHtml={(e as { is_proactive?: boolean }).is_proactive
            ? "" : (e.body_html || stripAttachmentDumps(e.body_text || ""))}
            originalIsPlain={!e.body_html && !/<\/[a-z]+>|<(html|body|div|p|br|table|td|tr|span|img|style|meta|!doctype)\b/i.test(e.body_text || "")}
            originalSender={e.sender || ""}
            originalDate={e.received_at || ""}
            prefilledTo={fwdPrefill.to}
            openSignal={fwdPrefill.signal}
          />
        </div>
      </div>

      </div>}{/* /ev2-detail-haupt */}

      <aside className={karte ? "ev2-detail-neben pk-neben" : "ev2-detail-neben ev2-mit-reitern"}
        hidden={karte && seite === "erledigen"}>
        {!karte && <div className="ev2-seiten-reiter" role="tablist" aria-label="Zur Mail">
          {([["notizen", "Notizen", e.notiz_anzahl || 0], ["kunde", "Kunde", 0],
             ["wissen", "Wissen", 0], ["verlauf", "Verlauf", 0]] as const).map(([k, l, n]) => (
            <button key={k} type="button" role="tab" aria-selected={seite === k}
              className={seite === k ? "aktiv" : undefined} onClick={() => setSeite(k)}>
              {l}{n > 0 && <span className="ev2-reiter-zahl">{n}</span>}
            </button>
          ))}
        </div>}
        {zeige("notizen") && e.email_direction !== "sent" && (
          <NotizenPanel emailId={emailId} tenant={e.tenant_slug} />
        )}
        {zeige("notizen") && e.email_direction === "sent" && (
          <div className="ev2-leise">Notizen gibt es an eingehenden Mails.</div>
        )}
        {zeige("kunde") && (<>
        {/* Termin-Einladung: uebernehmen, ohne Umweg (#29).
            🔴 Bewusst VOR dem Kontakt-Block und ausserhalb aller
            Verknuepfungs-Zweige. Der „Termin buchen"-Knopf weiter unten haengt
            am HERO-Kontakt — in Marlenes Fall („Noch nicht verknuepft") wurde
            der ganze Abschnitt gar nicht gerendert, und die einzige
            Moeglichkeit war „Plan erstellen". Genau das war die Meldung. */}
        {einladung && istUebernehmbar(einladung) && !e.hero_booking_id && (
          <div className="ev2-meta-block">
            <div className="ev2-meta-label">Termin-Einladung</div>
            <div style={{ fontSize: 12.5, marginBottom: 8, color: "var(--text-muted)" }}>
              {einladung.titel}
              {einladung.start && (
                <> · {einladung.start.slice(8, 10)}.{einladung.start.slice(5, 7)}.
                  {einladung.start.slice(0, 4)} {einladung.start.slice(11, 16)}</>
              )}
            </div>
            <button
              className="ev2-hero-ibtn ev2-hero-ibtn-primary"
              onClick={() => {
                setBlockerSlot(einladung.start)
                setBookOpen(true)
              }}
            >
              <CalendarClock className="w-3.5 h-3.5" /> Termin übernehmen
            </button>
          </div>
        )}
        {/* Im Text genannte Personen (#15). Vorschlag, kein Automatismus —
            angelegt wird erst im Dialog. */}
        {personen.length > 0 && (
          <div className="ev2-meta-block">
            <div className="ev2-meta-label">Im Text genannt</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {personen.slice(0, 4).map((p) => (
                <button
                  key={`${p.vorname}-${p.nachname}`}
                  type="button"
                  title={p.fundstelle}
                  onClick={() => {
                    setPersonPrefill({
                      first: p.vorname, last: p.nachname, phone: p.telefon || "",
                    })
                    setAnsprechpartnerOpen(true)
                  }}
                  style={{
                    textAlign: "left", cursor: "pointer", fontSize: 12.5,
                    padding: "5px 8px", borderRadius: 6,
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-card)", color: "var(--text)",
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {p.anrede} {[p.vorname, p.nachname].filter(Boolean).join(" ")}
                  </span>
                  {p.telefon && (
                    <span style={{ color: "var(--text-muted)" }}> · {p.telefon}</span>
                  )}
                  <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11.5 }}>
                    als Ansprechpartner anlegen
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="ev2-meta-block">
          <div className="ev2-meta-label">Kontakt</div>
          {/* HERO-Section — neutraler Card-Look. Bei fortytools eigene Karte
              (Kunde/Objekt); die HERO-Karte bleibt für HERO-Tenants unverändert. */}
          {isForty ? (
            <>
            <FortytoolsLinkCard
              emailId={emailId}
              info={fortyInfoQ.data}
              onRefetch={() => {
                qc.invalidateQueries({ queryKey: ["forty-link-info", emailId] })
                qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
              }}
              onCreateKundeObjekt={() => { setCreateProjectOnly(false); setCreateWithProj(true); setCreateOpen(true) }}
              onCreateKunde={() => { setCreateProjectOnly(false); setCreateWithProj(false); setCreateOpen(true) }}
              onCreateObjekt={() => { setCreateProjectOnly(true); setCreateWithProj(true); setCreateOpen(true) }}
            />
            {/* Terminbuchung (fortytools) — legt einen portal-eigenen Termin an */}
            <div className="ev2-meta-inhalt" style={{ marginTop: 14 }}>
              <div className="ev2-meta-label" style={{ marginBottom: 8 }}>Termin</div>
              <div>
                {e.hero_booking_id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, flexWrap: "wrap" }}>
                    <span style={{ color: "#059669" }}>✓ {e.booking_label || "Termin angelegt"}</span>
                    <a href={`/calendar?tenant=${e.tenant_slug}`} target="_blank" rel="noreferrer" style={{ color: "var(--accent-color)", fontSize: 12 }}>im Kalender öffnen</a>
                  </div>
                ) : (
                  <button className="ev2-hero-ibtn ev2-hero-ibtn-primary" onClick={() => setBookOpen(true)}>
                    <CalendarClock className="w-3.5 h-3.5" /> Termin buchen
                  </button>
                )}
              </div>
            </div>
            </>
          ) : (
          <div className="ev2-meta-inhalt">
            {/* #86: Rückmeldung zu einem offenen Telefonat — vor der HERO-Verknüpfung,
                weil genau das der Grund ist, warum die Mail hier liegt. */}
            <OffenerAnrufHinweis anruf={e.offener_anruf} emailId={emailId} />
            {/* #162: wann und wie zuletzt Kontakt zum Kunden war + Telefonat vermerken */}
            <LetzterKontakt emailId={emailId} />
            <div>
              {!(e.hero_contact_id || e.hero_project_id) ? (
                // Empty state: kompakt, eine Zeile + ein primaerer Button.
                // Darunter Vorschlaege (Historie / HERO-Adresstreffer), falls es welche gibt.
                <>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
                    Noch nicht verknüpft
                  </span>
                  <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
                    <button className="ev2-hero-ibtn ev2-hero-ibtn-primary"
                            onClick={() => setHeroOpen(true)} title="Kontakt/Projekt in HERO suchen">
                      <Search className="w-3.5 h-3.5" /> Verknüpfen
                    </button>
                    <button className="ev2-hero-ibtn"
                            onClick={() => { setCreateWithProj(true); setCreateOpen(true) }}
                            title="Neuen Kontakt + Projekt anlegen">
                      <Plus className="w-3.5 h-3.5" /> Kontakt + Projekt
                    </button>
                    <button className="ev2-hero-ibtn"
                            onClick={() => { setCreateWithProj(false); setCreateOpen(true) }}
                            title="Nur Kontakt anlegen (ohne Projekt)">
                      <Plus className="w-3.5 h-3.5" /> Nur Kontakt
                    </button>
                    {/* Mieter, Hausverwaltung, Ansprechpartner vor Ort — wer
                        schreibt, ist nicht immer der Kunde. Vorher blieb nur
                        „eigener Kontakt" (falsch) oder gar nichts. */}
                    <button className="ev2-hero-ibtn"
                            onClick={() => setAnsprechpartnerOpen(true)}
                            title="Absender:in als Ansprechpartner:in unter einem bestehenden Kunden anlegen">
                      <Plus className="w-3.5 h-3.5" /> Ansprechpartner:in
                    </button>
                  </div>
                </div>
                <LinkSuggestionsBox
                  emailId={emailId}
                  data={linkSuggQ.data}
                  onApplied={() => {
                    qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
                    qc.invalidateQueries({ queryKey: ["link-suggestions", emailId] })
                    qc.invalidateQueries({ queryKey: ["emails"] })
                  }}
                />
                {/* Der Vorgang steht auch ohne Kontakt zur Verfügung — bei den
                    Prod-Mails war das sogar der Normalfall: der Planer löst den
                    Kontakt selbst auf, geschrieben wird er nur beim Ausführen. */}
                <PlanVorgangZeile
                  emailId={emailId}
                  vorschlag={linkSuggQ.data?.vorgang_laut_plan}
                  kandidaten={linkSuggQ.data?.vorgang_kandidaten}
                  eindeutig={linkSuggQ.data?.vorgang_eindeutig}
                  onApplied={() => {
                    qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
                    qc.invalidateQueries({ queryKey: ["link-suggestions", emailId] })
                    qc.invalidateQueries({ queryKey: ["emails"] })
                  }}
                />
                </>
              ) : (
                <>
                  {/* Verknuepft-Zeile */}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>
                    <CheckCircle className="w-3.5 h-3.5" style={{ color: "#059669" }} />
                    {e.hero_contact_name && <strong>{e.hero_contact_name}</strong>}
                    {e.hero_contact_id && (
                      <HeroInfoPopover tenantSlug={e.tenant_slug || ""} contactId={e.hero_contact_id} projectId={e.hero_project_id}>
                        <a href={`/admin/open-hero/contact/${e.hero_contact_id}?tenant=${e.tenant_slug}`} target="_blank" style={{ color: "var(--accent-color)", fontSize: 12 }}>
                          in HERO öffnen
                        </a>
                      </HeroInfoPopover>
                    )}
                    {e.hero_project_id && (
                      <HeroInfoPopover tenantSlug={e.tenant_slug || ""} contactId={e.hero_contact_id} projectId={e.hero_project_id}>
                        <a href={`/admin/open-hero/project/${e.hero_project_id}?tenant=${e.tenant_slug}`} target="_blank" style={{ color: "var(--accent-color)", fontSize: 12 }}>
                          Vorgang in HERO öffnen
                        </a>
                      </HeroInfoPopover>
                    )}
                  </div>

                  {/* Termin-Banner wenn gebucht */}
                  {e.hero_booking_id && (
                    <div className="ev2-termin-zeile">
                      <CalendarClock className="w-3.5 h-3.5" />
                      <strong>Termin:</strong>
                      {e.booking_start_time ? (
                        <span>
                          {new Date(e.booking_start_time).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {e.booking_end_time && ` – ${new Date(e.booking_end_time).toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit" })}`}
                        </span>
                      ) : (
                        <span style={{ fontStyle: "italic" }}>{e.booking_label || "Zeit wird geladen…"}</span>
                      )}
                      {e.booking_title && <span style={{ color: "var(--text-muted)" }}>· {e.booking_title}</span>}
                      <a href={`/admin/calendar?event=${e.hero_booking_id}&tenant=${e.tenant_slug}`} target="_blank"
                         style={{ marginLeft: "auto", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 3 }}>
                        Kalender <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}

                  {/* Vorgang laut Plan — nur wenn noch keiner gesetzt ist */}
                  {!e.hero_project_id && (
                    <PlanVorgangZeile
                      emailId={emailId}
                      vorschlag={linkSuggQ.data?.vorgang_laut_plan}
                      kandidaten={linkSuggQ.data?.vorgang_kandidaten}
                      eindeutig={linkSuggQ.data?.vorgang_eindeutig}
                      onApplied={() => {
                        qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
                        qc.invalidateQueries({ queryKey: ["link-suggestions", emailId] })
                        qc.invalidateQueries({ queryKey: ["emails"] })
                      }}
                    />
                  )}

                  {/* Projekte des Kontakts (Status-Dropdowns inline) */}
                  {e.hero_contact_id && (
                    <HeroContactProjects emailId={emailId} currentProjectId={e.hero_project_id} tenantSlug={e.tenant_slug} />
                  )}

                  {/* Link-Actions (Kontakt aendern / Projekt wechseln / Alles aufheben) */}
                  {e.hero_contact_id && (
                    <HeroLinkActions
                      emailId={emailId}
                      hasProject={!!e.hero_project_id}
                      locked={!!e.hero_project_locked}
                      onOpenSearch={() => setHeroOpen(true)}
                      onAddProject={() => { setCreateProjectOnly(true); setCreateWithProj(true); setCreateOpen(true) }}
                    />
                  )}

                  {/* Primaerer Termin-Button */}
                  {!e.hero_booking_id && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        className="ev2-hero-ibtn ev2-hero-ibtn-primary"
                        onClick={() => setBookOpen(true)}
                        title="Termin anlegen"
                      >
                        <CalendarClock className="w-3.5 h-3.5" /> Termin buchen
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          )}
        </div>

        {(e.category || e.handlungsbedarf) && (
          <div className="ev2-meta-block">
            <div className="ev2-meta-label">Einordnung</div>
            {/* Tooltip „Üblich bei solchen Mails" (#83) — Kategorie × Gewerk laut Plan. */}
            {e.category && (
              <VorgehenChip tenantSlug={e.tenant_slug} kategorie={e.category} gewerk={planProject?.measure_name} className="ev2-meta-chip" />
            )}
            {e.handlungsbedarf && (
              <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.6, marginTop: 8 }}>
                {e.handlungsbedarf}
              </div>
            )}
          </div>
        )}

        {/* Liegt die Anfrage im Leistungsspektrum? (#82, Robin 08.09.2026) */}
        <LeistungsspektrumBlock emailId={emailId} tenantSlug={e.tenant_slug} />

        </>)}

        {/* Wissensdatenbank des Betriebs (Paul 01.10.2026) — seit 14.10. nur,
            was der Planer für diese Mail benutzt hat. */}
        {zeige("wissen") && (
          <BetriebswissenBlock emailId={emailId} tenantSlug={e.tenant_slug} />
        )}

        {zeige("verlauf") && (<>
        {/* #64 Teil 0: wie der Plan endete, was stattdessen geschah, warum —
            nur bei Mails, deren Plan schon einen Ausgang hat. */}
        {e.email_direction !== "sent" && <PlanAusgangBlock emailId={emailId} />}
        <div className="ev2-meta-block">
          <div className="ev2-meta-label">Verlauf</div>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.7 }}>
            {fmtDateLong(e.received_at)} · empfangen
          </div>
          <ActivityLog e={e} spalte />
          {/* #88: darüber steht, WAS an der Mail hängt — hier, WER es getan hat
              und wann. Marlene: „wer was zuletzt damit gemacht hat". */}
          <MailVerlauf emailId={emailId} />
          {/* Der Verlauf oben ist die Sicht des Portals. Was HERO wirklich hat,
              sagt nur HERO. */}
          <HeroAbgleich emailId={emailId} />
        </div>
        </>)}
      </aside>

      </div>{/* /ev2-detail-grid */}
      </div>{/* /ev2-detail-scroll */}

      {karte && (
        <KarteFuss emailId={emailId} status={e.status} zustaendig={e.zustaendig}
          wiedervorlageAm={e.wiedervorlage_am} eingehend={e.email_direction !== "sent"}
          onErledigt={onErledigt}>
          <ActionBar karte email={e} emailId={emailId} onClose={onClose} onReply={entwurfOeffnen}
            aufgabenText={aufgabenVorbelegung({
              monteurTask: planCache?.monteur_task,
              handlungsbedarf: e.handlungsbedarf,
              mailText: e.body_text,
            })}
            aufgabePlan={aufgabeAusPlan(planCache)}
            onQuickForward={(to) => setFwdPrefill({ to, signal: Date.now() })} />
        </KarteFuss>
      )}

      <HeroLinkModal
        open={heroOpen}
        onClose={() => setHeroOpen(false)}
        mode="email"
        itemId={emailId}
        tenantSlug={e.tenant_slug}
        currentContactId={e.hero_contact_id}
        currentContactName={e.hero_contact_name}
        currentProjectId={e.hero_project_id}
        invalidateKeys={[["email", String(emailId)], ["emails"], ["calendar"]]}
      />
      <BookingModal
        open={bookOpen}
        onClose={() => { setBookOpen(false); setBlockerSlot(null) }}
        emailId={emailId}
        proposedSlot={e.proposed_slot}
        prefillSlot={blockerSlot}
        prefillTitle={einladung && istUebernehmbar(einladung) ? einladung.titel : null}
        prefillDuration={einladung && istUebernehmbar(einladung) ? dauerAuswahl(einladung) : null}
        defaultTitle={e.hero_contact_name || e.subject || "Termin"}
        tenantSlug={e.tenant_slug}
        heroContactId={e.hero_contact_id}
        heroContactName={e.hero_contact_name}
        heroProjectId={e.hero_project_id}
        heroProjectLabel={e.hero_project_id ? "Verknüpfter Vorgang" : null}
        invalidateKeys={[["email", String(emailId)], ["emails"], ["calendar"]]}
      />
      {/* Vorbelegt aus dem Plan, falls einer da ist — sonst legt der Dialog ein
          Projekt ohne Gewerk, Typ und Pipeline-Status an. */}
      <CreateContactModal
        prefillMeasureId={planProject?.measure_id ?? null}
        prefillProjectTypeId={planProject?.project_type_id ?? null}
        prefillStepId={planProject?.type_id ?? null}
        prefillProjectName={planProject?.title || undefined}
        open={createOpen || contactRestore.restoreOpen}
        onClose={() => { setCreateOpen(false); setCreateProjectOnly(false); contactRestore.consume() }}
        mode="email"
        itemId={emailId}
        tenantSlug={(e as any).tenant_slug}
        initialWithProject={contactRestore.draft?.withProject ?? createWithProj}
        projectOnly={createProjectOnly || contactRestore.draft?.projectOnly}
        draftId={contactRestore.draft?.id}
        draftPrefill={contactRestore.draft || undefined}
        {...parseContactPrefill(e)}
        onSuccess={() => {
          /* invalidate handled in modal via queryKey — emails + email */
          qc.invalidateQueries({ queryKey: ["forty-link-info", emailId] })
        }}
      />
      <AnsprechpartnerModal
        open={ansprechpartnerOpen}
        onClose={() => { setAnsprechpartnerOpen(false); setPersonPrefill(null) }}
        emailId={emailId}
        tenantSlug={(e as any).tenant_slug || ""}
        prefill={personPrefill ? {
          // Aus dem Text genannt: keine Mailadresse, die haben wir nicht —
          // eine geratene waere schlimmer als ein leeres Feld.
          first: personPrefill.first,
          last: personPrefill.last,
          phone: personPrefill.phone,
          email: "",
        } : {
          first: parseContactPrefill(e).prefillFirst,
          last: parseContactPrefill(e).prefillLast,
          phone: parseContactPrefill(e).prefillPhone,
          email: parseContactPrefill(e).prefillEmail,
        }}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
          qc.invalidateQueries({ queryKey: ["link-suggestions", emailId] })
          qc.invalidateQueries({ queryKey: ["emails"] })
        }}
      />
    </>
  )
}

/** Kontakt-Infozeile mit Icons + Hover-Popup fuer vollstaendige Details.
 *  Zeigt inline: Email, Mobil, Adresse (gekuerzt). Bei Hover: alles lesbar aufgefaltet. */
